import React, { useRef, useState, useEffect } from 'react';

import { animated, useSpring } from '@react-spring/web';
import { config, formatLeadScriptContent } from 'views/LeadScriptEditor.js';
import moment from 'moment-timezone';
import update from 'immutability-helper';

import Abstract from 'classes/Abstract.js';
import AltFieldMapper, { validateRequiredFields } from 'views/AltFieldMapper.js';
import Appearance from 'styles/Appearance.js';
import Appointment from 'classes/Appointment.js';
import { AppointmentDetails } from 'managers/Appointments.js';
import Cookies from 'js-cookie';
import Client from 'classes/Client.js';
import Event from 'classes/Event.js';
import FieldMapper from 'views/FieldMapper.js';
import Layer, { EndIndex, LayerItem } from 'structure/Layer.js';
import Lead from 'classes/Lead.js';
import { LeadDetails } from 'managers/Leads.js';
import ListField from 'views/ListField.js';
import LottieView from 'views/Lottie.js';
import { Map } from 'views/MapElements.js';
import PageControl from 'views/PageControl.js';
import Panel from 'structure/Panel.js';
import Request from 'files/Request.js';
import Status from 'classes/Status.js';
import TextField from 'views/TextField.js';
import Utils from 'files/Utils.js';
import User from 'classes/User.js';
import { UserDetails } from 'managers/Users.js';
import Views, { AltBadge } from 'views/Main.js';
import UserLookupField from 'views/UserLookupField';
import LeadTypePickerField from 'views/LeadTypePickerField';
import LeadSubTypePickerField from 'views/LeadSubTypePickerField';

export const AddEditAppointmentSlot = ({ isNewTarget, onChange }, { abstract, index, options, utils }) => {

    const layerID = isNewTarget ? 'new_appointment_slot' : `edit_appointment_slot_${abstract.getID()}`;
    const [edits, setEdits] = useState({});
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);

    const onSubmitClick = async () => {
        try {

            // verify that all required fields were completed
            await validateRequiredFields(getFields);

            // verify that start hour is before the end hour
            if(edits.start_hour > edits.end_hour) {
                throw new Error('The start time must be before the end time');
            }

            // submit request to server
            await abstract.object.apply(utils, isNewTarget);

            // notify subscribers of data change
            if(typeof(onChange) === 'function') {
                onChange(abstract.object);
            }

            // show confirmation alert
            setLoading(false);
            utils.banner.show({
                title: 'Slot Saved',
                message: isNewTarget ? `Your new appointment slot has been created and is ready to use.` : `The '${abstract.object.name}' appointment slot has been updated.`,
                onShow: setLayerState.bind(this, 'close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue ${isNewTarget ? 'creating' : 'updating'} your appointment slot. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onUpdateTarget = props => {
        let edits = abstract.object.set(props);
        setEdits(edits);
    }

    const getButtons = () => {
        return [{
            color: 'primary',
            key: 'submit',
            loading: loading === 'submit',
            onClick: onSubmitClick,
            text: 'Submit'
        }];
    }

    const getFields = () => {
        return [{
            key: 'details',
            lastItem: true,
            title: 'Details',
            items: [{
                component: 'textfield',
                key: 'name',
                onChange: text => onUpdateTarget({ name: text }),
                title: 'Name',
                value: edits.name
            },{
                component: 'list',
                items: getHourItems(),
                key: 'start_hour',
                onChange: item => onUpdateTarget({ start_hour: item && item.id }),
                title: 'Start Time',
                value: edits.start_hour && formatHour(edits.start_hour)
            },{
                component: 'list',
                items: getHourItems(),
                key: 'end_hour',
                onChange: item => onUpdateTarget({ end_hour: item && item.id }),
                title: 'End Time',
                value: edits.end_hour && formatHour(edits.end_hour)
            }]
        }];
    }

    const getHourItems = () => {
        return [...Array(24)].map((_, index) => {
            return {
                id: index,
                title: formatHour(index)
            }
        });
    }

    const formatHour = val => {
        let hour = val > 12 ? val - 12 : val;
        return `${hour === 0 ? 12 : hour}:00 ${val > 11 ? 'PM' : 'AM'}`;
    }

    const setupTarget = () => {
        let edits = abstract.object.open();
        setEdits(edits);
    }

    useEffect(() => {
        setupTarget();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={'New Appointment Slot'}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            sizing: 'medium'
        }}>
            <AltFieldMapper
            fields={getFields()}
            utils={utils} />
        </Layer>
    )
}

export const AddEditEvent = ({ isNewTarget }, { abstract, index, options, utils }) => {

    const layerID = isNewTarget ? `new_event` : `edit_event_${abstract.getID()}`;
    const [evt, setEvent] = useState(null);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);

    const onSubmitClick = async () => {
        try {

            // set loading flag and validate required fields
            setLoading('submit');
            await validateRequiredFields(getFields);

            // submit request to server
            await abstract.object.apply(utils, isNewTarget);

            // end loading and show confirmation alert
            setLoading(false);
            utils.banner.show({
                title: 'Event Saved',
                message: `The "${evt.title}" ${evt.type.title} event has been ${isNewTarget ? 'created' : 'updated'}`,
                onShow: setLayerState.bind(this, 'close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue ${isNewTarget ? 'creating' : 'updating'} this event. ${e.message || 'An unknown error occurred'}`
            });
        }
        return;
    }

    const onUpdateTarget = props => {
        let edits = abstract.object.set(props);
        setEvent(edits);
    }

    const getButtons = () => {
        return [{
            color: 'primary',
            loading: loading === 'submit',
            key: 'done',
            onClick: onSubmitClick,
            text: isNewTarget ? 'Submit' : 'Save Changes'
        }];
    }

    const getFields = () => {
        if(!evt) {
            return [];
        }
        return [{
            key: 'details',
            title: 'About this Event',
            items: [{
                component: 'event_type_lookup',
                description: 'The event type helps categorize your events and allows you to easily filter the event on your calendar.',
                key: 'type',
                onChange: result => onUpdateTarget({ type: result }),
                title: 'Category',
                value: evt.type
            },{
                component: 'textfield',
                description: 'The title of the event is shown when representing the event in public and private settings.',
                key: 'title',
                onChange: text => onUpdateTarget({ title: text }),
                title: 'Name',
                value: evt.title
            }]
        },{
            key: 'user_and_schedule',
            title: 'Assignments and Scheduling',
            items: [{
                component: 'user_lookup',
                description: 'The primary assignment will see this event on their calendar and be notified of the event 1 hour and 2 hours before the start date.',
                key: 'primary_user',
                onChange: user => onUpdateTarget({ primary_user: user }),
                required: false,
                title: 'Closer',
                value: evt.primary_user
            },{
                component: 'date_duration_picker',
                description: 'This date will be used to chart the start date on your calendar.',
                key: 'start_date',
                onChange: date => {
                    onUpdateTarget({ 
                        end_date: evt.end_date && date > evt.end_date ? moment(date).add(1, 'hours') : evt.end_date,
                        start_date: date 
                    })
                },
                title: 'Start Date',
                value: evt.start_date
            },{
                component: 'date_duration_picker',
                description: 'This date will be used to chart the end date on your calendar.',
                key: 'end_date',
                onChange: date => onUpdateTarget({ end_date: date }),
                title: 'End Date',
                value: evt.end_date
            }]
        },{
            key: 'attachments',
            title: 'Attachments',
            items: [{
                component: 'lead_lookup',
                description: 'Attaching a lead to this event can be helpful if the event is tied to a lead in some way, like an installation that resulted from a lead in the client.',
                key: 'lead',
                onChange: result => onUpdateTarget({ lead: result }),
                required: false,
                title: 'Lead',
                value: evt.lead
            },{
                component: 'appointment_lookup',
                description: 'Attaching a appointment to this event can be helpful if the event is tied to a appointment in some way, like an installation that resulted from a successful appointment.',
                key: 'appointment',
                onChange: result => onUpdateTarget({ appointment: result }),
                required: false,
                title: 'Appointment',
                value: evt.appointment
            }]
        }];
    }

    const setupTarget = async () => {
        let edits = abstract.object.open();
        setEvent(edits);
    }

    useEffect(() => {
        setupTarget();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={isNewTarget ? `New Event` : `Editing ${abstract.getTitle()}`}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            sizing: 'medium'
        }}>
            <AltFieldMapper
            fields={getFields()} 
            utils={utils} />
        </Layer>
    )
}

export const AddEditEventType = ({ isNewTarget, onChange }, { abstract, index, options, utils }) => {

    const layerID = isNewTarget ? `new_event_type` : `edit_event_type_${abstract.getID()}`;
    const [eventType, setEventType] = useState(null);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);

    const onSubmitClick = async () => {
        try {

            // set loading flag and validate required fields
            setLoading('submit');
            await validateRequiredFields(getFields);

            // submit request to server
            await abstract.object.apply(utils, isNewTarget);

            // end loading and show confirmation alert
            setLoading(false);
            utils.banner.show({
                title: 'Event Type Saved',
                message: `The "${eventType.title}" event type has been ${isNewTarget ? 'created' : 'updated'}`,
                onShow: () => {
                    setLayerState('close');
                    if(typeof(onChange) === 'function') {
                        onChange(abstract.object);
                    }
                }
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue ${isNewTarget ? 'creating' : 'updating'} this event type. ${e.message || 'An unknown error occurred'}`
            });
        }
        return;
    }

    const onUpdateTarget = props => {
        let edits = abstract.object.set(props);
        setEventType(edits);
    }

    const getButtons = () => {
        return [{
            color: 'primary',
            loading: loading === 'submit',
            key: 'done',
            onClick: onSubmitClick,
            text: isNewTarget ? 'Submit' : 'Save Changes'
        }];
    }

    const getFields = () => {
        if(!eventType) {
            return [];
        }
        return [{
            key: 'details',
            title: 'About this Event Type',
            items: [{
                component: 'textfield',
                description: 'What do you want to call this event type? ',
                key: 'title',
                onChange: text => onUpdateTarget({ title: text }),
                title: 'Name',
                value: eventType.title
            },{
                component: 'icon_picker',
                key: 'icon',
                onChange: icon => onUpdateTarget({ icon: icon }),
                props: { category: 'event_type' },
                title: 'Icon',
                value: eventType.icon
            }]
        }];
    }

    const setupTarget = () => {
        let edits = abstract.object.open();
        setEventType(edits);
    }

    useEffect(() => {
        setupTarget();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={isNewTarget ? `New Event Type` : `Editing ${abstract.getTitle()}`}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            sizing: 'medium'
        }}>
            <AltFieldMapper
            fields={getFields()} 
            utils={utils} />
        </Layer>
    )
}

export const AddEditLeadScript = ({ isNewTarget, onAddLeadScript }, { abstract, index, options, utils }) => {

    const layerID = isNewTarget ? 'new_lead_script' : `edit_lead_script_${abstract.getID()}`;
    const [loading, setLoading] = useState(false);
    const [layerState, setLayerState] = useState(null);
    const [script, setScript] = useState(null);

    const onDoneClick = async () => {
        try {

            // set loading flag and verify that required fields were filled out
            setLoading('done');
            await validateRequiredFields(getFields);

            // determine if a new target needs to be created
            if(isNewTarget) {

                // submit request to server
                await abstract.object.submit(utils);

                // end loading and show confirmation alert
                setLoading(false);
                utils.banner.show({
                    title: 'Script Created',
                    message: `The "${abstract.object.title}" lead script has been created`,
                    onShow: () => {
                        setLayerState('close');
                        if(typeof(onAddLeadScript) === 'function') {
                            onAddLeadScript(abstract.object);
                        }
                    }
                });
                return;
            }

            // submit request to server
            await abstract.object.update(utils);

            // end loading and show confirmation alert
            setLoading(false);
            utils.banner.show({
                title: 'Script Updated',
                message: `The "${abstract.object.title}" lead script has been updated`,
                onShow: setLayerState.bind(this, 'close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue ${isNewTarget ? 'creating' : 'updating'} this lead script. ${e.message || 'An unknown error occurred'}`
            });
        }
        return;
    }

    const onUpdateTarget = props => {
        let edits = abstract.object.set(props);
        setScript(edits);
    }

    const getButtons = () => {
        return [{
            color: 'primary',
            key: 'done',
            loading: loading === 'done',
            onClick: onDoneClick,
            text: isNewTarget ? 'Done' : 'Save Changes'
        }];
    }

    const getFields = () => {
        if(!script) {
            return [];
        }
        return [{
            key: 'details',
            title: 'Script Details',
            items: [{
                component: 'lead_script_editor',
                description: 'The script content will be shown to a setter during their conversation with a lead.',
                key: 'text',
                onChange: script => onUpdateTarget({ text: script }),
                required: true,
                title: 'Content',
                value: script.text,
            },{
                component: 'client_lookup',
                description: 'Which client does this script belong to?',
                key: 'client',
                onChange: client => onUpdateTarget({ client: client }),
                required: false,
                title: 'Client',
                value: script.client,
                visible: isNewTarget && utils.user.get().level < User.levels.get().primary
            },{
                component: 'textfield',
                description: 'The title for a lead script is used to identify this content.',
                key: 'title',
                onChange: text => onUpdateTarget({ title: text }),
                required: true,
                title: 'Title',
                value: script.title
            }]
        }];
    }

    const setupTarget = async () => {
        try {

             // declare client and target edits
            let client = utils.client.get();
            let edits = abstract.object.open();

            // automatically set client to current client if user is not an administrator
            if(isNewTarget === true && utils.user.get().level > User.levels.get().admin) {
                edits = abstract.object.set({ client: client });
            }

            // determine how to set client object for existing targets
            if(isNewTarget === false) {
                if(abstract.object.client_id === client.id) {
                    edits = abstract.object.set({ client: client });
                } else {
                    let nextClient = await Client.get(utils, client.id);
                    edits = abstract.object.set({ client: nextClient });
                }
            }
            setScript(edits);

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue setting up this lead script. ${e.message || 'An unknown error occurred'}`,
                onClick: setLayerState.bind(this, 'close')
            });
        }
    }

    useEffect(() => {
        setupTarget();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={isNewTarget ? 'New Script' : `Editing ${abstract.getTitle()}`}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            sizing: 'medium'
        }}>
            <AltFieldMapper
            fields={getFields()} 
            utils={utils}/>
        </Layer>
    )
}

export const AddEditStatus = ({ isNewTarget }, { abstract, index, options, utils }) => {

    const layerID = isNewTarget ? `new_status` : `edit_status_${abstract.getID()}`;
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(null);

    const onDoneClick = async () => {
        try {

            // set loading flag and validate required fields
            setLoading('submit');
            await validateRequiredFields(getFields);

            // submit changes to server
            await abstract.object.apply(utils, isNewTarget);

            // prepare list of capabilities for confirmation alert
            let capabilities = Object.keys(status.capabilities).filter(key => {
                return status.capabilities[key];
            });

            // show confirmation alert
            setLoading(false);
            utils.alert.show({
                title: 'All Done!',
                message: `The "${status.text}" status has been ${isNewTarget ? 'created' : 'updated'}. This status will be available for ${Utils.oxfordImplode(capabilities)}`,
                onClick: setLayerState.bind(this, 'close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue ${isNewTarget ? 'creating' : 'updating'} this status. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onUpdateTarget = props => {
        let edits = abstract.object.set(props);
        setStatus(edits);
    }

    const getButtons = () => {
        return [{
            color: 'primary',
            key: 'submit',
            loading: loading === 'submit',
            onClick: onDoneClick,
            text: isNewTarget ? 'Submit' : 'Save Changes'
        }]
    }

    const getFields = () => {
        if(!status) {
            return [];
        }
        return [{
            key: 'details',
            title: 'About this Status',
            items: [{
                component: 'textfield',
                description: 'What do you want to call this status? ',
                key: 'title',
                onChange: text => onUpdateTarget({ text: text }),
                title: 'Name',
                value: status.text
            },{
                component: 'color_picker',
                description: 'What color do you want to associate with this status? ',
                key: 'color',
                onChange: color => onUpdateTarget({ color: color }),
                title: 'Color',
                value: status.color && status.color.toUpperCase()
            }]
        },{
            key: 'capabilities',
            title: 'Capabilities',
            items: ['appointments','events','leads'].map(key => {
                let title = Utils.ucFirst(key);
                return {
                    component: 'bool_list',
                    description: `Do you want to make this status available for ${title}?`,
                    key: key,
                    onChange: val => {
                        onUpdateTarget({ 
                            capabilities: update(status.capabilities, {
                                [key]: {
                                    $set: val
                                }
                            }) 
                        });
                    },
                    required: false,
                    title: Utils.ucFirst(title),
                    value: status.capabilities[key]
                }
            })
        }];
    }

    const setupTarget = () => {
        let edits = abstract.object.open();
        setStatus(edits);
    }

    useEffect(() => {
        setupTarget();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={isNewTarget ? `New Status` : `Editing ${abstract.getTitle()}`}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            sizing: 'medium'
        }}>
            <AltFieldMapper
            fields={getFields()}
            utils={utils} />
        </Layer>
    )
}

export const AddEditLeadType = ({ isNewTarget }, { abstract, index, options, utils }) => {

    const layerID = isNewTarget ? `new_lead_type` : `edit_lead_type_${abstract.getID()}`;
    const [layerState, setLayerState] = useState(null);
    const [leadType, setLeadType] = useState(null);
    const [loading, setLoading] = useState(false);

    const onCreateLeadType = async props => {
        try {
            await abstract.object.submit(utils, props);
            setLoading(false);
            utils.banner.show({
                title: 'Lead Type Created',
                message: `The "${leadType.text}" lead type has been created`,
                onShow: () => setLayerState('close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue creating your lead type. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const onCreateSpecificLeadType = () => {
        utils.alert.show({
            title: 'New Lead Type',
            message: `Would you like to add this lead type to ${utils.client.get().name} or make it globally available for all clients?`,
            buttons: [{
                key: 'global',
                title: 'Add Globally',
                style: 'default',
                visible: utils.user.get().level <= User.levels.get().admin
            },{
                key: 'client',
                title: 'Add to Client Workspace',
                style: 'default',
                visible: utils.user.get().level <= User.levels.get().admin
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: async key => {
                if(key === 'cancel') {
                    setLoading(false);
                    return;
                }
                try {
                    await Utils.sleep(0.25);
                    onCreateLeadType({ for_client: key !== 'global' });
                } catch(e) {
                    console.error(e.message);
                }
            }
        })
    }

    const onDoneClick = async () => {
        try {
            setLoading('done');
            await Utils.sleep(0.25);
            await validateRequiredFields(getFields);

            // create target
            if(isNewTarget) {
                /*if(utils.user.get().level <= User.levels.get().admin) {
                    onCreateSpecificLeadType();
                    return;
                }*/
                onCreateLeadType();
                return;
            }

            // update target
            await abstract.object.update(utils);
            setLoading(false);
            utils.banner.show({
                title: 'Lead Type Updated',
                message: `The "${leadType.text}" lead type has been updated`,
                onShow: () => setLayerState('close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue ${isNewTarget ? 'creating' : 'updating'} this lead type. ${e.message || 'An unknown error occurred'}`
            })
        }
        return;
    }

    const onUpdateTarget = props => {
        let edits = abstract.object.set(props);
        setLeadType(edits);
    }

    const getButtons = () => {
        return [{
            color: 'primary',
            key: 'done',
            loading: loading === 'done',
            onClick: onDoneClick,
            text: isNewTarget ? 'Done' : 'Save Changes'
        }]
    }

    const getFields = () => {

        if(!leadType) {
            return [];
        }

        let items = [{
            key: 'details',
            title: 'About this Lead Type',
            items: [{
                component: 'textfield',
                description: 'What do you want to call this lead type? ',
                key: 'text',
                onChange: text => onUpdateTarget({ text: text }),
                title: 'Name',
                value: leadType.text
            },{
                component: 'icon_picker',
                key: 'icon',
                onChange: icon => onUpdateTarget({ icon: icon }),
                props: { category: 'lead_type' },
                title: 'Icon',
                value: leadType.icon
            },{
                component: 'lead_script_picker',
                description: 'Attaching a script to a lead type will automatically present the script if no other script is found for a given lead.',
                key: 'lead_script',
                onChange: script => onUpdateTarget({ lead_script: script }),
                required: false,
                title: 'Lead Script',
                value: leadType.lead_script ? leadType.lead_script.title : null
            }]
        }]

        return items;
    }

    const setupTarget = () => {
        let edits = abstract.object.open();
        setLeadType(edits);
    }

    useEffect(() => {
        setupTarget();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={isNewTarget ? `New Lead Type` : `Editing ${abstract.getTitle()}`}
        utils={utils}
        options={{
            ...options,
            sizing: 'medium',
            layerState: layerState,
            loading: loading === true,
        }}>
            <AltFieldMapper
            fields={getFields()}
            utils={utils} />
        </Layer>
    )
}

export const Clients = ({ index, options, utils }) => {

    const panelID = 'clients';
    const limit = 15;
    const showInactiveRef = useRef(false);

    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(null);
    const [offset, setOffset] = useState(0);
    const [paging, setPaging] = useState(null);
    const [searchText, setSearchText] = useState(null);
    const [showInactive, setShowInactive] = useState(false);

    const onChangeActiveStatus = async (client, evt) => {

        // prevent parent click from triggering
        evt.stopPropagation();

        // confirmation not needed when activating a previously deactivated client
        let next = !client.active;
        if(next === true) {
            onChangeActiveStatusConfirm(client);
            return;
        }

        // request confirmation before deactivating a client
        utils.alert.show({
            title: `Deactivate Client`,
            message: `Are you sure that you want to deactivate this client? This will prevent "${client.name}" from using LifeWipe.`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onChangeActiveStatusConfirm(client);
                    return;
                }
            }
        });
    }

    const onChangeActiveStatusConfirm = async client => {
        try {

            // send status update request to server
            let next_status = !client.active;
            await Request.post(utils, '/clients/', {
                type: 'set_active_status',
                id: client.id,
                active: next_status
            });

            // update local client object with new status
            client.active = next_status;

            // notify subscribers of data change
            utils.content.fetch('clients');
            utils.content.update({
                type: 'clients',
                object: client
            });

            // update list of clients
            setClients(clients => {
                return clients.map(prevClient => {
                    if(prevClient.id === client.id) {
                        prevClient.active = next_status;
                    }
                    return prevClient;
                });
            });

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue changing the status of this Client. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onClientClick = client => {
        utils.layer.open({
            id: `client_details_${client.id}`,
            abstract: Abstract.create({
                type: 'client',
                object: client
            }),
            Component: ClientDetails
        })
    }

    const onPrintClients = async props => {
        return new Promise(async (resolve, reject) => {
            try {
                setLoading(true);
                let { clients } = await Request.get(utils, '/clients/', {
                    type: 'all',
                    search_text: searchText,
                    ...props
                });

                setLoading(false);
                resolve(clients.map(client => Client.create(client)));

            } catch(e) {
                setLoading(false);
                reject(e);
            }
        })
    }

    const getActiveStatus = client => {
        if(!client) {
            return;
        }
        let color = client.active ? Appearance.colors.primary() : Appearance.colors.grey();
        return (
            <div
            className={'text-button'}
            onClick={onChangeActiveStatus.bind(this, client)}
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: 100,
                height: '100%',
                maxWidth: 75,
                textAlign: 'center',
                border: `1px solid ${color}`,
                background: Appearance.colors.softGradient(color),
                borderRadius: 5,
                overflow: 'hidden'
            }}>
                <span style={{
                    ...Appearance.textStyles.subTitle(),
                    color: 'white',
                    fontWeight: '600',
                    width: '100%'
                }}>{client.active ? 'Active' : 'Not Active'}</span>
            </div>
        )
    }

    const getButtons = () => {
        if(utils.user.get().level < User.levels.get().primary) {
            return [{
                key: 'active',
                title: `${showInactiveRef.current ? 'Hide' : 'Show'} Inactive`,
                style: showInactiveRef.current ? 'default' : 'grey',
                onClick: () => {
                    setOffset(0);
                    showInactiveRef.current = showInactive ? false : true;
                    setShowInactive(showInactiveRef.current);
                }
            }];
        }
    }

    const getContent = () => {
        if(clients.length === 0) {
            return (
                Views.entry({
                    title: 'No Clients Found',
                    subTitle: 'No clients were found in the system',
                    bottomBorder: false,
                    hideIcon: true
                })
            )
        }
        return (
            <table
            className={'px-3 py-2 m-0'}
            style={{
                width: '100%'
            }}>
                <thead style={{
                    width: '100%'
                }}>
                    {getFields()}
                </thead>
                <tbody style={{
                    width: '100%'
                }}>
                    {clients.map((client, index) => {
                        return getFields(client, index)
                    })}
                </tbody>
            </table>
        )
    }

    const getFields = (client, index) => {

        let target = client || {};
        let fields = [{
            key: 'name',
            title: 'Name',
            value: target.name
        },{
            key: 'locality',
            title: 'City',
            value: target.address ? target.address.locality : 'Not available'
        },{
            key: 'administrative_area_level_1',
            title: 'State',
            value: target.address ? target.address.administrative_area_level_1 : 'Not available'
        },{
            key: 'primary',
            title: 'Primary User',
            value: target.primary ? target.primary.full_name : 'Not available'
        },{
            key: 'phone_number',
            title: 'Phone Number',
            value: target.primary ? target.primary.phone_number : null
        },{
            key: 'status',
            title: 'Status',
            value: getActiveStatus(target)
        }];
        if(!client) {
            return (
                <tr style={{
                    borderBottom: `1px solid ${Appearance.colors.divider()}`
                }}>
                {fields.map((field, index) => {
                    return (
                        <td
                        key={index}
                        className={'px-3 py-2 flexible-table-column'}>
                            <span style={{
                                ...Appearance.textStyles.title()
                            }}>{field.title}</span>
                        </td>
                    )
                })}
                </tr>
            )
        }
        return (
            <tr
            key={index}
            className={`view-entry ${window.theme}`}
            style={{
                borderBottom: index !== clients.length - 1 && `1px solid ${Appearance.colors.divider()}`
            }}>
            {fields.map((field, index) => {
                return (
                    <td
                    key={index}
                    className={'px-3 py-2 flexible-table-column'}
                    onClick={onClientClick.bind(this, client)}>
                        <span style={Appearance.textStyles.subTitle()}>{field.value}</span>
                    </td>
                )
            })}
            </tr>
        )
    }

    const getPrintProps = () => {
        return {
            onFetch: onPrintClients,
            onRenderItem: (item, index, items) => ({
                name: item.name,
                city: item.address ? item.address.locality : null,
                administrative_area_level_1: item.address ? item.address.administrative_area_level_1 : null,
                primary: item.primary ? item.primary.full_name : null,
                phone_number: item.primary ? item.primary.phone_number : null,
                status: item.active ? 'Yes' : 'No'
            }),
            headers: [{
                key: 'name',
                title: 'Name'
            },{
                key: 'locality',
                title: 'City'
            },{
                key: 'administrative_area_level_1',
                title: 'State'
            },{
                key: 'primary',
                title: 'Primary User'
            },{
                key: 'phone_number',
                title: 'Phone Number'
            },{
                key: 'status',
                title: 'Status'
            }]
        }
    }

    const fetchClients = async () => {
        try {
            setLoading(true);
            let { clients, paging } = await Request.get(utils, '/clients/', {
                type: 'all',
                limit: limit,
                offset: offset,
                search_text: searchText,
                show_inactive: showInactiveRef.current
            });

            setLoading(false);
            setPaging(paging);
            setClients(clients.map(client => Client.create(client)))

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue loading the clients list. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    useEffect(() => {
        fetchClients();
        showInactiveRef.current = showInactive;
    }, [offset, searchText, showInactive]);

    useEffect(() => {
        utils.events.on(panelID, 'client_change', fetchClients);
        utils.content.subscribe(panelID, ['clients'], {
            onFetch: fetchClients,
            onUpdate: abstract => {
                setClients(clients => {
                    return clients.map(client => {
                        return client.id === abstract.getID() ? abstract.object : client
                    })
                })
            }
        });
        return () => {
            utils.content.unsubscribe(panelID);
            utils.events.off(panelID, 'client_change', fetchClients);
        }
    }, []);

    return (
        <Panel
        index={index}
        title={'Clients'}
        utils={utils}
        options={{
            ...options,
            buttons: getButtons(),
            loading: loading,
            search: {
                placeholder: 'Search by name, primary, city, state, or zipcode...',
                onChange: text => setSearchText(text)
            },
            print: getPrintProps(),
            paging: {
                data: paging,
                limit: limit,
                offset: offset,
                onClick: next => setOffset(next)
            }
        }}>
            <div style={{
                ...Appearance.styles.unstyledPanel()
            }}>
                {getContent()}
            </div>
        </Panel>
    )
}

export const ClientDetails = ({ abstract, index, options, utils }) => {

    const layerID = `client_details_${abstract.getID()}`;
    const [loading, setLoading] = useState(false);
    const [layerState, setLayerState] = useState(null);

    const onPrimaryUserClick = async () => {
        try {
            let user = await User.get(utils, abstract.object.primary.user_id);
            utils.layer.open({
                abstract: Abstract.create({
                    object: user,
                    type: 'users'
                }),
                Component: UserDetails,
                id: `user_details_${user.user_id}`,
                permissions: ['users.details']
            })
        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the information for this account. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onInvalidateCaches = () => {
        utils.alert.show({
            title: 'Invalidate Caches',
            message: `Are you sure that you want to invalidate the caches for this client? This will force the system to refresh all cached data for this client. This action can not be undone.`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onInvalidateCachesConfirm();
                    return;
                }
            }
        });
    }

    const onInvalidateCachesConfirm = async () => {
        try {

            setLoading('options');
            await Request.post(utils, '/clients/', {
                client_id: abstract.getID(),
                type: 'invalidate_caches'
            });

            setLoading(false);
            utils.banner.show({
                title: 'Caches Invalidated',
                message: `The caches for this client have been successfully invalidated. Changes should be reflected shortly.`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue invalidating the caches for this client. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onOptionsClick = evt => {
        utils.sheet.show({
            items: [{
                key: 'invalidate_caches',
                title: 'Invalidate Caches',
                style: 'default',
                visible: utils.user.get().level <= User.levels.get().admin
            },{
                key: 'status',
                title: `Set as ${abstract.object.active ? 'Inactive' : 'Active'}`,
                style: abstract.object.active ? 'destructive' : 'default'
            }],
            target: evt.target
        }, key => {
            if(key === 'invalidate_caches') {
                onInvalidateCaches();
                return;
            }
            if(key === 'status') {
                onSetStatus();
                return;
            }
        })
    }

    const onSetStatus = () => {
        utils.alert.show({
            title: 'Confirm New Status',
            message: `Are you sure that you want to set this Client as ${abstract.object.active ? 'inactive' : 'active'}`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: abstract.object.active ? 'destructive' : 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onSetStatusConfirm();
                    return;
                }
            }
        });
    }

    const onSetStatusConfirm = async () => {
        try {
            setLoading(true);
            await Utils.sleep(0.25);

            let next_status = !abstract.object.active;
            await Request.post(utils, '/clients/', {
                type: 'set_active_status',
                id: abstract.getID(),
                active: next_status
            })

            setLoading(false);
            abstract.object.active = next_status;
            utils.content.update(abstract);

            utils.banner.show({
                title: 'Client Status Changed',
                message: `This Client has been ${next_status ? 'activated' : 'deactivated'}`,
                onShow: () => setLayerState('close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue updating the status for this Client. ${e.message || 'An unknown error occurred'}`,
            })
        }
    }

    const getButtons = () => {
        if(utils.user.get().level <= User.levels.get().admin) {
            return [{
                color: 'secondary',
                key: 'options',
                onClick: onOptionsClick,
                text: 'Options'
            }];
        }
    }

    const getFields = () => {

        let client = abstract.object;
        let items = [{
            key: 'details',
            title: 'Details',
            items: [{
                key: 'id',
                title: 'ID',
                value: client.id
            },{
                key: 'name',
                title: 'Name',
                value: client.name
            },{
                key: 'primary',
                title: 'Primary User',
                value: client.primary ? client.primary.full_name : null,
                onClick: client.primary ? onPrimaryUserClick : null
            }]
        },{
            key: 'location',
            title: 'Location',
            items: [{
                key: 'location',
                title: 'Location',
                component: 'map',
                visible:  client.address && client.location ? true : false,
                value: client.location
            },{
                key: 'address',
                title: 'Physical Address',
                value: client.address ? Utils.formatAddress(client.address) : null
            },{
                key: 'timezone',
                title: 'Timezone',
                value: client.timezone
            }]
        }]

        return items;
    }

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        title={'Client Details'}
        index={index}
        utils={utils}
        options={{
            ...options,
            loading: loading,
            layerState: layerState,
            sizing: 'medium'
        }}>
            <FieldMapper 
            sections={getFields()} 
            utils={utils} />
        </Layer>
    )
}

export const ClientLocationsMap = ({ index, options, utils }) => {

    const panelID = 'client_locations_map';
    const [loading, setLoading] = useState(false);
    const [locationData, setLocationData] = useState(null);
    const [region, setRegion] = useState(null);
    const [showInactive, setShowInactive] = useState(false);

    const onClientClick = async props => {
        try {
            let client = await Client.get(utils, props.id);
            utils.layer.open({
                id: `client_details_${client.id}`,
                abstract: Abstract.create({
                    type: 'client',
                    object: client
                }),
                Component: ClientDetails
            })
        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the information for this Client. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const getButtons = () => {
        if(utils.user.get().level < User.levels.get().primary) {
            return [{
                key: 'active',
                title: `${showInactive ? 'Hide' : 'Show'} Inactive`,
                style: showInactive ? 'default' : 'grey',
                onClick: () => setShowInactive(status => !status)
            }]
        };
        return null;
    }

    const getFeatures = () => {
        if(!locationData) {
            return null;
        }
        return {
            id: 'client_locations',
            region: region,
            data: {
                ...locationData,
                features: locationData.features.filter(feature => {
                    if(showInactive) {
                        return true;
                    }
                    return feature.properties.active === true;
                })
            },
            onClick: onClientClick,
            icons: [{
                key: 'location-icon-grey',
                path: 'images/location-icon-grey.png'
            }],

            layer: {
                type: 'symbol',
                layout: {
                    'icon-size': 0.2,
                    'icon-anchor': 'center',
                    'icon-image': 'location-icon-grey',
                    'icon-allow-overlap': true
                },
                paint: {
                    'icon-color': [ 'get', 'color' ]
                }
            },
            onHover: feature => {
                try {
                    let { id, name, address } = feature.properties;
                    return {
                        title: name,
                        subTitle: address
                    }
                } catch(e) {
                    console.error(e.message);
                }
            }
        }
    }

    const fetchLocations = async () => {
        try {
            setLoading(true);
            let { data, region } = await Request.get(utils, '/clients/', {
                type: 'locations',
                show_inactive: showInactive
            });

            setLoading(false);
            setRegion(region);
            setLocationData(data);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue loading the location map data. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    useEffect(() => {
        fetchLocations();
    }, [showInactive]);

    useEffect(() => {
        utils.content.subscribe(panelID, ['clients'], {
            onFetch: fetchLocations,
            onUpdate: abstract => {
                setLocationData(data => {
                    if(!data) {
                        return data;
                    }
                    // attempt to find client in feature collection
                    // may not be present if client was previously inactive
                    for(var i in data.features) {
                        if(abstract && data.features[i].properties.id) {

                            // update active status
                            data.features[i].properties.active = abstract.object.active;
                            data.features[i].properties.color = abstract.object.active ? Appearance.colors.primary() : Appearance.colors.lightGrey;
                            return { ...data };
                        }
                    }
                    // add to feature collection if not found above and location data is present
                    if(abstract.object.location) {
                        data.features.push({
                            type: 'Feature',
                            geometry: {
                                type: 'Point',
                                coordinates: [ abstract.object.location.longitude, abstract.object.location.latitude ]
                            },
                            properties: {
                                id: abstract.getID(),
                                name: abstract.object.name,
                                address: Utils.formatAddress(abstract.object.address),
                                active: abstract.object.active,
                                color: abstract.object.active ? Appearance.colors.primary() : Appearance.colors.lightGrey
                            }
                        })
                        return { ...data };
                    }
                    return data;
                })
            }
        });
        return () => {
            utils.content.unsubscribe(panelID);
        }
    }, []);

    return (
        <Panel
        index={index}
        title={'Client Locations'}
        options={{
            ...options,
            loading: loading,
            buttons: getButtons()
        }}>
            <Map
            features={getFeatures()}
            isScrollEnabled={true}
            isZoomEnabled={true}
            isRotationEnabled={true}
            style={{
                border: `1px solid ${Appearance.colors.divider()}`,
                height: 350,
                width: '100%'
            }}/>
        </Panel>
    )
}

export const ClientPreferences = ({ abstract, index, options, utils }) => {

    const layerID = `client_preferences_${abstract.getID()}`;
    const [loading, setLoading] = useState('init');
    const [layerState, setLayerState] = useState(null);
    const [prefs, setPrefs] = useState(null);

    const onDoneClick = async () => {
        try {

            setLoading('done');
            await Utils.sleep(0.25);
            let { preferences } = await Request.post(utils, '/clients/', {
                type: 'update_preferences',
                id: abstract.getID(),
                prefs: {
                    global_lead_visibility: prefs.global_lead_visibility,
                    global_appointment_visibility: prefs.global_appointment_visibility,
                    global_appointment_request_visibility: prefs.global_appointment_request_visibility,
                    global_card_visibility: prefs.global_card_visibility,
                    operating_hours: prefs.operating_hours,
                    release_on_submission: prefs.release_on_submission,
                    default_appointment_duration: prefs.default_appointment_duration,
                    appointment_notification: prefs.appointment_notification,
                    appointment_request_notification: prefs.appointment_request_notification,
                    appointment_rescheduled_notification: prefs.appointment_rescheduled_notification,
                    duplicate_rescheduled_appointments: prefs.duplicate_rescheduled_appointments,
                    global_marketing: prefs.global_marketing,
                    global_appointment_marketing: prefs.global_appointment_marketing,
                    global_appointment_request_marketing: prefs.global_appointment_request_marketing,
                    owner_referral_sms_content: prefs.owner_referral_sms_content,
                    connect_default_status: prefs.connect_default_status,
                    vapi_ai_calls: prefs.vapi_ai_calls
                }
            });

            // end loading and update local state
            setLoading(false);
            setPrefs(preferences);

            // update client preferences state 
            utils.client.update_preferences(preferences);

            // show confirmation alert
            utils.banner.show({
                title: 'Settings Updated',
                message: `The settings for the ${abstract.object.name} client workspace have been updated`,
                onShow: () => setLayerState('close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue updating the preferences for ${abstract.object.name}. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onItemClick = item => {

        // prepare preference for item
        let isOperatingHours = item.key.includes('operating_hours');
        let preference = isOperatingHours ? prefs.operating_hours : prefs[item.key];

        // open preference editor
        utils.layer.open({
            Component: ClientPreferenceEditor.bind(this, {
                item,
                onChange: result => {
                    setPrefs(prefs => ({ 
                        ...prefs, 
                        [isOperatingHours ? 'operating_hours' : item.key]: result 
                    }));
                },
                preference,
            }),
            id: `client_preference_editor_${item.key}`
        });
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            key: 'cancel',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Cancel'
        },{
            color: 'primary',
            key: 'done',
            loading: loading === 'done',
            onClick: onDoneClick,
            permissions: ['settings.actions.edit'],
            text: 'Save Changes'
        }];
    }

    const getPropFields = () => {

        if(!prefs) {
            return [];
        }

        let items = [{
            key: 'appointments',
            title: 'Appointments',
            collapsed: false,
            items: [{
                description: `This is the default amount of time that will be allocated for a newly set Appointment. You'll still have the opportunity to change the duration of the Appointment when booking.`,
                key: 'default_appointment_duration',
                title: 'Default Duration (Hours)'
            },{
                description: 'Enabling this feature will preserve the original appointment timeslot when rescheduling a appointment for a new timeslot. The original timeslot will be listed as "Rescheduled" and the new timeslot will be listed as "Set".',
                key: 'duplicate_rescheduled_appointments',
                title: 'Duplicate Rescheduled Appointments'
            },{
                description: 'By default, setters are only able to interact with appointments that have been assigned to them. If needed, you can let your setters interact with all the appointments in your client workspace.',
                key: 'global_appointment_marketing',
                title: 'Global Marketing'
            },{
                description: 'Enabling this feature will provide Appointment visibility to all accounts. Disabling this feature will only show Appointments to accounts who hold an assignment, booking credit, ride along assignment, trainee assignment, or partner assignment.',
                key: 'global_appointment_visibility',
                title: 'Global Visibility'
            },{
                description: 'Enabling this feature will tell the system to automatically send a push notification to the Primary User when a Appointment has been rescheduled.',
                key: 'appointment_rescheduled_notification',
                title: 'Notification: Rescheduled '
            },{
                description: 'Enabling this feature will tell the system to automatically send a push notification to the Primary User when a Appointment has been set.',
                key: 'appointment_notification',
                title: 'Notification: Set'
            },{
                description: 'Enabling this rule will allow the client to book a appointment up to three days in advance. This means that any bookings that are further than three days out will not be accepted.',
                key: 'three_day_rule',
                title: 'Three Day Rule'
            }]
        },{
            key: 'leads',
            title: 'Leads',
            collapsed: false,
            items: [{
                description: 'The marketing connect has "Not Home" set as the default status for new leads. While you can change this to any status you prefer, this setting controls the initial status assigned to new leads.',
                key: 'connect_default_status',
                title: 'Connect Default Status'
            },{
                description: 'By default, new leads are made available to the client after they are submitted. If needed, you can automatically mark new leads as "Unreleased" when they are submitted. This will only allow primarys and directors to view this lead until the release it to the rest of the client.',
                key: 'owner_referral_sms_content',
                title: 'Owner Referral Text Message Content'
            },{
                description: 'Enabling this feature will provide lead visibility to all accounts. Disabling this feature will only show Leads to accounts who hold lead credit, setter credit, or enrollment credit.',
                key: 'global_lead_visibility',
                title: 'Global Visibility'
            },{
                description: 'By default, setters are only able to interact with leads that have been assigned to them. If needed, you can let your setters interact with all the leads in your client workspace.',
                key: 'global_marketing',
                title: 'Global Marketing'
            },{
                description: 'By default, new leads are made available to the client after they are submitted. If needed, you can automatically mark new leads as "Unreleased" when they are submitted. This will only allow primarys and directors to view this lead until the release it to the rest of the client.',
                key: 'release_on_submission',
                title: 'Release on Submission'
            }]
        },{
            key: 'operating_hours',
            title: 'Operating Hours',
            collapsed: false,
            items: [{
                description: 'The start and end time for your operating hours dictates when a appointment can be scheduled. These times also control the available timeslots for scheduling calendars.',
                key: 'operating_hours.appointments',
                title: 'Appointments',
                value: prefs.operating_hours ? `${moment(prefs.operating_hours.appointments.start, 'HH:mm:ss').format('h:mma')} to ${moment(prefs.operating_hours.appointments.end, 'HH:mm:ss').format('h:mma')}` : null,
            },{
                description: `The start and end time for these operating hours dictate when calls should be made. These times determine when the system will allow calls to be completed.`,
                key: 'operating_hours.calls',
                title: 'Calls',
                value: prefs.operating_hours ? `${moment(prefs.operating_hours.calls.start, 'HH:mm:ss').format('h:mma')} to ${moment(prefs.operating_hours.calls.end, 'HH:mm:ss').format('h:mma')}` : null,
            }]
        },{
            key: 'vapi_ai',
            title: 'Vapi AI',
            collapsed: false,
            items: [{
                description: 'This determines whether or not the Vapi AI setter(s) will make automated calls when leads are assigned to them.',
                key: 'vapi_ai_calls',
                title: 'Allow Automated Calls'
            },{
                description: `The start and end time for these operating hours dictate when calls should be made. These times determine when the system will allow calls to be completed.`,
                key: 'operating_hours.vapi_ai_calls',
                title: 'Operating Hours',
                value: prefs.operating_hours ? `${moment(prefs.operating_hours.vapi_ai_calls.start, 'HH:mm:ss').format('h:mma')} to ${moment(prefs.operating_hours.vapi_ai_calls.end, 'HH:mm:ss').format('h:mma')}` : null,
            }]
        }];

        return items.map(section => {
            section.items = section.items.filter(item => {
                return item.visible !== false;
            }).map(item => {
                return {
                    ...item,
                    color: prefs[item.key]?.enabled ? Appearance.colors.green : null,
                    component: item.component || 'textfield',
                    onClick: item.onClick || onItemClick.bind(this, item),
                    value: prefs[item.key]?.enabled ? 'Enabled' : 'Disabled',
                    ...getSpecialtyItemOverview(item)
                }
            })
            return section;
        });
    }

    const getSpecialtyItemOverview = item => {
        
        let { appointments, calls, vapi_ai_calls } = prefs.operating_hours || {};
        switch(item.key) {
            case 'default_appointment_duration':
            return { 
                color: null,
                value: `${prefs[item.key]?.value || 0} ${prefs[item.key]?.value === 1 ? 'Hour' : 'Hours'}` 
            }

            case 'connect_default_status':
            return { 
                color: null,
                value: prefs[item.key]?.text || 'Not Set'
            }

            case 'owner_referral_sms_content':
            return {
                color: null,
                value: 'Click to Configure'
            }

            case 'operating_hours.appointments':
            return { 
                color: null,
                value: `${moment(appointments.start, 'HH:mm:ss').format('h:mma')} to ${moment(appointments.end, 'HH:mm:ss').format('h:mma')}`
            }

            case 'operating_hours.calls':
            return { 
                color: null,
                value: `${moment(calls.start, 'HH:mm:ss').format('h:mma')} to ${moment(calls.end, 'HH:mm:ss').format('h:mma')}`
            }

            case 'operating_hours.vapi_ai_calls':
            return { 
                color: null,
                value: `${moment(vapi_ai_calls.start, 'HH:mm:ss').format('h:mma')} to ${moment(vapi_ai_calls.end, 'HH:mm:ss').format('h:mma')}`
            }
        }
    }

    const fetchPreferences = async () => {
        try {
            let { preferences } = await Request.get(utils, '/clients/', {
                type: 'preferences',
                id: abstract.getID()
            });
            console.log(preferences);
            setLoading(false);
            setPrefs(preferences);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the preferences for ${abstract.object.name}. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const getContent = () => {

        // determine if a loading component is needed
        if(loading === 'init') {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    height: 125,
                    justifyContent: 'center',
                    padding: 15,
                    width: '100%'
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 50,
                        width: 50
                    }}/>
                </div>
            )
        }

        return (
            <FieldMapper
            editable={true}
            sections={getPropFields()}
            utils={utils}/>
        )
    }

    useEffect(() => {
        fetchPreferences();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={`Settings for ${abstract.object.name}`}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            sizing: 'medium'
        }}>
            {getContent()}
        </Layer>
    )
}

export const ClientPreferenceEditor = ({ item, onChange, preference },{ index, options, utils }) => {

    const layerID = `client_preference_editor_${item.key}`;
    const [edits, setEdits] = useState(preference);
    const [excludeUsers, setExcludeUsers] = useState([]);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);

    const onDoneClick = async () => {
        try {

            // verify that required values were provided
            await validateRequiredFields(getFields);

            // format users array in payload for subscribers
            let payload = { 
                ...edits,
                ...excludeUsers.length > 0 && {
                    exclude_users: excludeUsers.map(user => user.user_id)
                }
            }

            // close layer and notify subscribers of data change
            setLayerState('close');
            if(typeof(onChange) === 'function') {
                onChange(payload);
            }

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue updating the ${item.title} preference. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onUpdateTarget = props => {
        setEdits(prev => ({ ...prev, ...props }));
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            key: 'cancel',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Cancel'
        },{
            color: 'primary',
            key: 'done',
            onClick: onDoneClick,
            text: 'Save Changes'
        }];
    }

    const getFields = () => {

        // determine if appointment duration is being configured
        if(item.key === 'default_appointment_duration') {
            return [{
                id: 'configuration',
                title: 'Configuration',
                items: [{
                    component: 'textfield',
                    key: 'default_appointment_duration',
                    onChange: text => onUpdateTarget({ value: text?.length > 0 ? text : null }),
                    props: item.props,
                    title: 'Default Duration (Hours)',
                    value: edits.value
                }]
            }];
        }

        // determine if a first responders textview is needed
        if(item.key === 'owner_referral_sms_content') {
            return [{
                id: 'configuration',
                title: 'Configuration',
                items: [{
                    component: 'textview',
                    key: 'owner_referral_sms_content',
                    onChange: text => onUpdateTarget({ value: text }),
                    props: item.props,
                    title: 'Owner Referral SMS Content',
                    required: edits.enabled,
                    value: edits.value
                }]
            }];
        }

        // determine if a status code selection needs to take place for connect_default_status
        if(item.key === 'connect_default_status') {  

            // prepare status code list options for current client
            let items = utils.client.status_codes.get().filter(entry => {
                return entry.capabilities.leads;
            }).map(entry => ({
                id: entry.code,
                title: entry.text
            }));

            return [{
                id: 'configuration',
                title: 'Configuration',
                items: [{
                    component: 'list',
                    items: items,
                    key: 'connect_default_status',
                    onChange: item => {
                        onUpdateTarget({ 
                            text: item?.title,
                            value: item?.id 
                        });
                    },
                    title: 'Default Status',
                    value: edits.value && items.find(entry => entry.id === edits.value)?.title,
                }]
            }];
        }

        // determine if operating hours were requested
        if(item.key.includes('operating_hours') === true) {
            let key = item.key.split('.')[1];
            return [{
                id: 'configuration',
                title: 'Configuration',
                items: [{
                    component: 'time_range_picker',
                    key: key,
                    props: {
                        end: edits[key]?.end,
                        increments: 60,
                        onEndChange: time => {
                            onUpdateTarget({ 
                                [key]: {
                                    ...edits[key],
                                    end: time.format('HH:mm:ss')
                                }
                            });
                        },
                        onStartChange: time => {
                            onUpdateTarget({ 
                                [key]: {
                                    ...edits[key],
                                    start: time.format('HH:mm:ss')
                                }
                            });
                        },
                        start: edits[key]?.start
                    },
                    title: 'Client',
                    value: edits[key]?.end && edits[key]?.start ? true : false
                }]
            }];
        }

        // prepare default items for entry
        let items = [{
            component: 'bool_list',
            key: 'enabled',
            onChange: val => onUpdateTarget({ enabled: val }),
            title: 'Enabled',
            required: false,
            value: edits.enabled
        }];

        // determine if users can be excluded from this preference
        if(item.key.includes('global') === true) {
            items.push({
                component: 'multiple_user_lookup',
                key: 'exclude_users',
                onChange: setExcludeUsers,
                title: 'Exclude Users',
                required: false,
                value: excludeUsers
            })
        };

        // return section with configured items
        return [{
            id: 'configuration',
            title: 'Configuration',
            items: items
        }];
    }

    const fetchUsers = async () => {
        try {
            setLoading(true);
            let { users } = await Request.get(utils, '/clients/', {
                type: 'get_users'
            });

            console.log(users);
            setLoading(false);
            setExcludeUsers(users.filter(user => preference.exclude_users.includes(user.user_id)));

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue loading the users list. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    useEffect(() => {
        if(preference.exclude_users?.length > 0) {
            fetchUsers();
        }
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={item.title}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            sizing: 'medium'
        }}>
            <LayerItem 
            collapsed={false}
            title={'About this Setting'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    padding: '8px 12px 8px 12px',
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.key(),
                        whiteSpace: 'normal'
                    }}>{item.description}</span>
                </div>
            </LayerItem>
            <AltFieldMapper
            fields={getFields()}
            utils={utils} />
        </Layer>
    )
}

export const ClientSelector = ({ index, options, utils }) => {

    const layerID = 'client_selector';
    const limit = 5;
    const offset = useRef(0);

    const [loading, setLoading] = useState(true);
    const [layerState, setLayerState] = useState(null);
    const [searchText, setSearchText] = useState(null);
    const [paging, setPaging] = useState(null);
    const [clients, setClients] = useState([]);
    const [defaultClientID, setDefaultClientID] = useState(null);

    const onClientClick = async client => {
        try {

            // fetch details for client
            setLoading(true);
            let result = await Client.get(utils, client.id);

            // end loading and update root with new client 
            setLoading(false);
            utils.client.set(result);

            // close layer
            setLayerState('close');
            
        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the details for this client. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const getBadge = client => {
        let badges = [];
        if(client.id === defaultClientID) {
            badges.push({
                text: 'Selected',
                color: Appearance.colors.primary()
            });
        }
        if(!client.active) {
            badges.push({
                text: 'Not Active',
                color: Appearance.colors.grey()
            });
        }
        return badges.length > 0 ? badges : null;
    }

    const fetchClients = async () => {
        try {
            
            let { clients, paging } = await Request.get(utils, '/clients/', {
                limit: limit,
                offset: offset.current,
                search_text: searchText,
                type: 'all'
            });

            setLoading(false);
            setPaging(paging);
            setClients(clients.map(client => Client.create(client)));

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the clients list. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const setupTarget = async () => {
        try {
            let clientID = Cookies.get('default_client_id');
            if(clientID) {
                setDefaultClientID(parseInt(clientID));
                return;
            }
            setDefaultClientID(utils.user.get().client.id);
        } catch(e) {
            console.error(e.message);
        }
    }

    useEffect(() => {
        fetchClients();
    }, [searchText]);

    useEffect(() => {
        setupTarget();
    }, [])

    return (
        <Layer
        id={layerID}
        index={index}
        utils={utils}
        options={{
            ...options,
            loading: loading === true,
            sizing: 'medium',
            layerState: layerState,
            removePadding: true,
            sticky: {
                top: (
                    <div style={{
                        borderBottom: `1px solid ${Appearance.colors.divider()}`,
                        padding: 15
                    }}>
                        <TextField
                        icon={'search'}
                        value={searchText}
                        useDelay={true}
                        placeholder={'Search by name, primary, city, state, or zipcode...'}
                        onChange={text => {
                            offset.current = 0;
                            setLoading(true);
                            setSearchText(text);
                        }}
                        containerStyle={{
                            width: '100%'
                        }}/>
                    </div>
                ),
                bottom: paging && (
                    <PageControl
                    data={paging}
                    limit={limit}
                    loading={loading === 'paging'}
                    offset={offset}
                    onClick={next => {
                        offset.current = next;
                        setLoading('paging');
                        fetchClients();
                    }}/>
                )
            }
        }}>
            <div style={{
                padding: 15
            }}>
                {clients.length > 0
                    ?
                    <div style={{
                        ...Appearance.styles.unstyledPanel()
                    }}>
                        {clients.map((client, index) => {
                            return (
                                Views.entry({
                                    key: index,
                                    title: client.name,
                                    subTitle: client.address ? Utils.formatAddress(client.address) : 'Address Not Available',
                                    badge: getBadge(client),
                                    hideIcon: true,
                                    bottomBorder: index !== clients.length - 1 ,
                                    onClick: onClientClick.bind(this, client)
                                })
                            )
                        })}
                    </div>
                    :
                    <div style={Appearance.styles.unstyledPanel()}>
                        {Views.entry({
                            title: 'No Clients Found',
                            subTitle: 'There are no clients available to view',
                            bottomBorder: false,
                            hideIcon: true
                        })}
                    </div>
                }
            </div>
        </Layer>
    )
}

export const EventDetails = ({ abstract, index, options, utils }) => {

    const layerID = `event_details_${abstract.getID()}`;
    const [evt, setEvent] = useState(abstract.object);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);

    const onDeleteClick = () => {

        // prevent moving forward if feature is disabled for current user
        if(utils.user.permissions.get('events.actions.delete') === false) {
            return utils.user.permissions.reject();
        }

        // show alert requesting confirmation
        utils.alert.show({
            title: 'Delete Event',
            message: 'Are you sure that you want to delete this event? This can not be undone.',
            buttons: [{
                key: 'confirm',
                title: 'Delete',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    setTimeout(onDeleteConfirm, 250);
                    return;
                }
            }
        });
    }

    const onDeleteConfirm = async () => {
        try {

            // set loading flag and submit request to server
            setLoading('options');
            await Request.post(utils, '/events/', {
                id: abstract.getID(),
                type: 'delete'
            });

            // notify subscribers that new data is available
            utils.content.fetch('events');

            // end loading and show confirmation alert
            setLoading(false);
            utils.banner.show({
                title: 'Event Deleted',
                message: `The "${abstract.object.title}" ${abstract.object.type.title} event has been deleted`,
                onShow: setLayerState.bind(this, 'close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue deleting this event. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onAppointmentClick = async () => {
        try {

            // start loading and fetch appointment details
            setLoading(true);
            let appointment = await Appointment.get(utils, evt.appointment.id);

            // end loading and show appointment details layer
            setLoading(false);
            utils.layer.open({
                id: `appointment_details_${appointment.id}`,
                abstract: Abstract.create({
                    type: 'appointments',
                    object: appointment
                }),
                Component: AppointmentDetails
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the information for this appointment. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onEditClick = () => {

        // prevent moving forward if feature is disabled for current user
        if(utils.user.permissions.get('events.actions.edit') === false) {
            return utils.user.permissions.reject();
        }

        // open event editing layer
        utils.layer.open({
            id: `edit_event_${abstract.getID()}`,
            abstract: abstract,
            Component: AddEditEvent.bind(this, {
                isNewTarget: false
            })
        });
    }

    const onLeadClick = async () => {
        try {

            // start loading and fetch lead details
            setLoading(true);
            let lead = await Lead.get(utils, evt.lead.id);

            // end loading and show lead details layer
            setLoading(false);
            utils.layer.open({
                abstract: Abstract.create({
                    object: lead,
                    type: 'leads'
                }),
                Component: LeadDetails,
                id: `lead_details_${lead.id}`,
                permissions: ['leads.details']
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the information for this lead. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onOptionsClick = evt => {
        utils.sheet.show({
            items: [{
                key: 'delete',
                permissions: ['events.actions.delete'],
                title: 'Delete',
                style: 'destructive'
            },{
                key: 'status',
                permissions: ['events.actions.status'],
                title: 'Set Status',
                style: 'default'
            }],
            target: evt.target
        }, key => {
            if(key === 'delete') {
                onDeleteClick();
                return;
            }
            if(key === 'status') {
                utils.layer.open({
                    abstract: abstract,
                    Component: SetEventStatus,
                    id: `set_event_status_${abstract.getID()}`,
                    permissions: ['events.actions.edit']
                });
            }
        });
    }

    const onUserClick = async userID => {
        try {

            // start loading and fetch user details
            setLoading(true);
            let user = await User.get(utils, userID);

            // end loading and show user details layer
            setLoading(false);
            utils.layer.open({
                abstract: Abstract.create({
                    object: user,
                    type: 'users'
                }),
                Component: UserDetails,
                id: `user_details_${user.user_id}`,
                permissions: ['users.details']
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the account information for this user. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const getAttachments = () => {

        // prepare list of attachments
        let { attachments = [] } = evt.props;

        // add appointment to list of attachments if applicable
        if(evt.appointment) {
            attachments.push({
                badge: evt.appointment.status,
                bottomBorder: evt.lead ? true : false,
                icon: { path: evt.appointment.lead_type && evt.appointment.lead_type.icon.url },
                key: 'appointment',
                onClick: onAppointmentClick,
                subTitle: evt.appointment.phone_number || 'No phone number provided',
                title: `Appointment for ${evt.appointment.full_name}`
            });
        }

        // add lead to list of attachments if applicable
        if(evt.lead) {
            attachments.push({
                badge: evt.lead.status,
                bottomBorder: false,
                icon: { path: evt.lead.lead_type && evt.lead.lead_type.icon.url },
                key: 'lead',
                onClick: onLeadClick,
                subTitle: evt.lead.phone_number || 'No phone number provided',
                title: `Lead for ${evt.lead.full_name}`
            });
        }

        // prevent moving forward if no attachments were added
        if(attachments.length === 0) {
            return null;
        }

        return (
            <LayerItem 
            collapsed={false}
            title={'Attachments'}>
                {attachments.sort((a,b) => {
                    return a.title.localeCompare(b.title);
                }).map((attachment, index) => {
                    return (
                        <div 
                        key={index}
                        style={{
                            ...Appearance.styles.unstyledPanel(),
                            marginBottom: 8,
                            width: '100%'
                        }}>
                            {Views.entry(attachment)}
                        </div>
                    )
                })}
            </LayerItem>
        )
    }

    const getButtons = () => {
        return [{
            color: 'secondary',
            key: 'options',
            loading: loading === 'options',
            onClick: onOptionsClick,
            text: 'Options'
        },{
            color: 'primary',
            key: 'edit',
            onClick: onEditClick,
            text: 'Edit',
        }];
    }

    const getFields = () => {
        return [{
            key: 'details',
            title: 'Details',
            items: [{
                key: 'type',
                title: 'Category',
                value: evt.type && evt.type.title
            },{
                key: 'date',
                title: 'Created',
                value: evt.date && Utils.formatDate(evt.date)
            },{
                key: 'user_id',
                onClick: evt.user ? onUserClick.bind(this, evt.user.user_id) : null,
                title: 'Created By',
                value: evt.user && evt.user.full_name
            },{
                key: 'id',
                title: 'ID',
                value: evt.id
            },{
                key: 'title',
                title: 'Title',
                value: evt.title
            },{
                color: evt.status && evt.status.color,
                key: 'status',
                title: 'Status',
                value: evt.status && evt.status.text
            }]
        },{
            key: 'user_and_schedule',
            title: 'Assignments and Scheduling',
            items: [{
                key: 'primary_user',
                onClick: evt.primary_user ? onUserClick.bind(this, evt.primary_user.user_id) : null,
                title: 'Closer',
                value: evt.primary_user && evt.primary_user.full_name
            },{
                key: 'start_date',
                title: 'Start Date',
                value: evt.start_date && Utils.formatDate(evt.start_date)
            },{
                key: 'end_date',
                title: 'End Date',
                value: evt.end_date && Utils.formatDate(evt.end_date)
            }]
        }];
    }

    useEffect(() => {
        utils.content.subscribe(layerID, ['appointments', 'appointment_status', 'events', 'leads', 'lead_status', 'lead_types'], {
            onUpdate: next => {
                switch(next.type) {
                    case 'appointment':
                    if(abstract.object.appointment && abstract.object.appointment.id === next.getID()) {
                        abstract.object.appointment = {
                            ...abstract.object.appointment,
                            end_date: next.object.end_date,
                            full_name: next.object.lead.full_name,
                            phone_number: next.object.lead.phone_number,
                            start_date: next.object.start_date,
                            status: next.object.status 
                        }
                        setEvent(abstract.object);
                    }
                    break;

                    case 'appointment_status':
                    if(abstract.object.appointment && abstract.object.appointment.id === next.getID()) {
                        abstract.object.appointment = {
                            ...abstract.object.appointment,
                            status: next.object.status 
                        }
                        setEvent(abstract.object);
                    }
                    break;

                    case 'event':
                    if(abstract.getID() === next.getID()) {
                        abstract.object.end_date = next.object.end_date;
                        abstract.object.primary_user = next.object.primary_user;
                        abstract.object.start_date = next.object.start_date;
                        abstract.object.status = next.object.status;
                        abstract.object.title = next.object.title;
                        abstract.object.type = next.object.type;
                        abstract.object.user = next.object.user;
                        setEvent(abstract.object);
                    }
                    break;

                    case 'lead':
                    if(abstract.object.appointment && abstract.object.appointment.lead_id === next.getID()) {
                        abstract.object.appointment = {
                            ...abstract.object.appointment,
                            full_name: next.object.full_name,
                            lead_type: next.object.lead_type,
                            phone_number: next.object.phone_number
                        }
                        setEvent(abstract.object);
                    }
                    if(abstract.object.lead && abstract.object.lead.id === next.getID()) {
                        abstract.object.lead = {
                            ...abstract.object.lead,
                            full_name: next.object.full_name,
                            lead_type: next.object.lead_type,
                            phone_number: next.object.phone_number,
                            status: next.object.status 
                        }
                        setEvent(abstract.object);
                    }
                    break;

                    case 'lead_status':
                    if(abstract.object.lead && abstract.object.lead.id === next.getID()) {
                        abstract.object.lead = {
                            ...abstract.object.lead,
                            status: next.object.status 
                        }
                        setEvent(abstract.object);
                    }
                    break;

                    case 'lead_type':
                    if(abstract.object.appointment && abstract.object.appointment.lead_type.id === next.getID()) {
                        abstract.object.appointment.lead_type = next.object;
                        setEvent(abstract.object);
                    }
                    if(abstract.object.lead && abstract.object.lead.lead_type.id === next.getID()) {
                        abstract.object.lead.lead_type = next.object;
                        setEvent(abstract.object);
                    }
                    break;
                }
            }
        });
        return () => {
            utils.content.unsubscribe(layerID);
        }

    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={abstract.getTitle()}
        utils={utils}
        options={{
            ...options,
            extension: {
                abstract: abstract,
                permissions: {
                    delete: ['events.notes.actions.delete'],
                    edit: ['events.notes.actions.edit'],
                    new: ['events.notes.actions.new'],
                    view: ['events.notes']
                },
                type: 'notes'
            },
            layerState: layerState,
            loading: loading,
            sizing: 'medium'
        }}>
            <FieldMapper
            sections={getFields()} 
            utils={utils} />
            {getAttachments()}
        </Layer>
    )
}

export const EventTypeDetails = ({ abstract, index, options, utils }) => {

    const layerID = `event_type_details_${abstract.getID()}`;
    const [eventType, setEventType] = useState(abstract.object);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);

    const onChangeActiveStatus = () => {
        utils.alert.show({
            title: `${abstract.object.active ? 'Deactivate' : 'Activate'} Event Type`,
            message: `Are you sure that you want to ${abstract.object.active ? 'deactivate' : 'activate'} this event type?`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: abstract.object.active ? 'destructive' : 'default'
            },{
                key: 'cancel',
                title: `Do Not ${abstract.object.active ? 'Deactivate' : 'Activate'}`,
                style: abstract.object.active ? 'default' : 'destructive'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    setTimeout(onChangeActiveStatusConfirm, 250);
                    return;
                }
            }
        });
    }

    const onChangeActiveStatusConfirm = async () => {
        try {

            // submit request to server
            setLoading(true);
            let next_status = !abstract.object.active;
            await Request.post(utils, '/events/', {
                active: next_status,
                id: abstract.getID(),
                type: 'set_type_active_status',
            });

            // update active status for target and notify subscribers of data change
            abstract.object.active = next_status;
            utils.content.update(abstract);

            // end loading and show confirmation alert
            setLoading(false);
            utils.banner.show({
                title: 'Event Type Status Changed',
                message: `This event type has been ${next_status ? 'activated' : 'deactivated'}`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue updating the active status for this event type. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onDeleteClick = () => {
        utils.alert.show({
            title: 'Delete Event Type',
            message: 'Are you sure that you want to delete this event type? This can not be undone.',
            buttons: [{
                key: 'confirm',
                title: 'Delete',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    setTimeout(onDeleteLeadTypeConfirm, 250);
                    return;
                }
            }
        });
    }

    const onDeleteLeadTypeConfirm = async () => {
        try {

            // set loading flag and submit request to server
            setLoading(true);
            await Request.post(utils, '/events/', {
                id: abstract.getID(),
                type: 'delete_type',
            });

            // notify subscribers that new data is available
            utils.content.fetch('event_types');

            // end loading and show confirmation alert
            setLoading(false);
            utils.banner.show({
                title: 'Event Type Deleted',
                message: `The "${abstract.object.title}" event type has been deleted`,
                onShow: setLayerState.bind(this, 'close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue deleting this event type. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onEditClick = () => {
        utils.layer.open({
            abstract: abstract,
            Component: AddEditEventType.bind(this, {
                isNewTarget: false
            }),
            id: `edit_event_type_${abstract.getID()}`,
            permissions: ['events.actions.edit']
        });
    }

    const onOptionsClick = evt => {
        utils.sheet.show({
            items: [{
                key: 'active',
                style: abstract.object.active ? 'destructive' : 'default',
                title: abstract.object.active ? 'Deactivate' : 'Activate'
            },{
                key: 'delete',
                visible: abstract.object.client_id ? true : false,
                style: 'destructive',
                title: 'Delete from Client'
            }],
            target: evt.target
        }, key => {
            if(key === 'active') {
                onChangeActiveStatus();
                return;
            }
            if(key === 'delete') {
                onDeleteClick();
                return;
            }
        });
    }

    const getButtons = () => {
        return [{
            color: 'secondary',
            key: 'options',
            loading: loading === 'options',
            onClick: onOptionsClick,
            text: 'Options'
        },{
            color: 'primary',
            key: 'edit',
            onClick: onEditClick,
            text: 'Edit',
        }];
    }

    const getFields = () => {
        return [{
            key: 'details',
            title: 'Details',
            items: [{
                key: 'icon',
                title: 'Icon',
                visible: eventType.icon ? true : false,
                value: eventType.icon && (
                    <img 
                    src={eventType.icon.url} 
                    style={{
                        borderRadius: 10,
                        height: 20,
                        objectFit: 'cover',
                        width: 20 
                    }}/>
                )
            },{
                key: 'id',
                title: 'ID',
                value: eventType.id
            },{
                key: 'title',
                title: 'Name',
                value: eventType.title
            },{
                key: 'active',
                title: 'Status',
                value: eventType.active ? 'Active' : 'Inactive'
            }]
        }];
    }

    useEffect(() => {
        utils.content.subscribe(layerID, ['event_types'], {
            onUpdate: next => {
                setEventType(next.getID() === abstract.getID() ? next.object : abstract.object);
            }
        })
        return () => {
            utils.content.unsubscribe(layerID);
        }

    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={abstract.getTitle()}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading,
            sizing: 'medium'
        }}>
            <FieldMapper
            sections={getFields()} 
            utils={utils} />
        </Layer>
    )
}

export const EventTypesList = ({ index, options, utils }) => {

    const panelID = 'event_types_list';
    const limit = 15;
    const offset = useRef(0);

    const [eventTypes, setEventTypes] = useState([]);
    const [loading, setLoading] = useState(null);
    const [paging, setPaging] = useState(null);
    const [searchText, setSearchText] = useState(null);

    const onChangeActiveStatus = async (type, evt) => {
        try {
            evt.stopPropagation();
            let status = !type.active;
            await Request.post(utils, '/events/', {
                active: status,
                id: type.id,
                type: 'set_type_active_status'
            });

            setEventTypes(types => {
                return types.map(prev => {
                    if(prev.id === type.id) {
                        prev.active = status;
                    }
                    return prev;
                });
            });

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue changing the active status for this event type. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onNewEventType = () => {
        utils.layer.open({
            abstract: Abstract.create({
                object: Event.Type.new(),
                type: 'event_type'
            }),
            Component: AddEditEventType.bind(this,{
                isNewTarget: true
            }),
            id: 'new_event_type',
            permissions: ['events.actions.new']
        });
    }

    const onEventTypeClick = type => {
        utils.layer.open({
            id: `event_type_details_${type.id}`,
            abstract: Abstract.create({
                object: type,
                type: 'event_type'
            }),
            Component: EventTypeDetails
        });
    }

    const getButtons = () => {
        return [{
            key: 'new',
            onClick: onNewEventType,
            style: 'default',
            title: 'New Event Type'
        }];
    }

    const getContent = () => {
        if(eventTypes.length === 0) {
            return Views.entry({
                bottomBorder: false,
                hideIcon: true,
                subTitle: 'No custom event types were found in the system',
                title: 'No Event Types Found'
            })
        }
        return eventTypes.map((type, index) => {
            return (
                Views.entry({
                    bottomBorder: index !== eventTypes.length - 1,
                    key: index,
                    onClick: onEventTypeClick.bind(this, type),
                    rightContent: (
                        <div
                        className={'text-button'}
                        onClick={onChangeActiveStatus.bind(this, type)}
                        style={{
                            alignItems: 'center',
                            background: Appearance.colors.softGradient(type.active ? Appearance.colors.primary() : Appearance.colors.grey()),
                            border: `1px solid ${type.active ? Appearance.colors.primary() : Appearance.colors.grey()}`,
                            borderRadius: 5,
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            justifyContent: 'center',
                            maxWidth: 75,
                            overflow: 'hidden',
                            textAlign: 'center',
                            width: 100
                        }}>
                            <span style={{
                                ...Appearance.textStyles.subTitle(),
                                color: 'white',
                                fontWeight: '600',
                                width: '100%'
                            }}>{type.active ? 'Active' : 'Not Active'}</span>
                        </div>
                    ),
                    subTitle: `Created ${moment(type.date).format('MMMM Do, YYYY [at] h:mma')}`,
                    title: type.title,
                    icon: {
                        path: type.icon.url,
                        imageStyle: {
                            backgroundColor: Appearance.colors.transparent
                        }
                    },
                })
            )
        });
    }

    const fetchEventTypes = async () => {
        try {
            setLoading(true);
            let { event_types, paging } = await Request.get(utils, '/events/', {
                limit: limit,
                offset: offset.current,
                search_text: searchText,
                show_inactive: true,
                type: 'types'
            });

            setLoading(false);
            setPaging(paging);
            setEventTypes(event_types.map(type => Event.Type.create(type)));

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the event types list. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    useEffect(() => {
        fetchEventTypes();
    }, [searchText]);

    useEffect(() => {
        utils.events.on(panelID, 'client_change', fetchEventTypes);
        utils.content.subscribe(panelID, ['event_types'], {
            onFetch: fetchEventTypes,
            onUpdate: abstract => {
                setEventTypes(types => {
                    return types.map(type => {
                        return type.id === abstract.getID() ? abstract.object : type;
                    })
                });
            }
        })
        return () => {
            utils.events.off(panelID, 'client_change');
            utils.content.unsubscribe(panelID);
        }

    }, []);

    return (
        <Panel
        index={index}
        title={'Event Types'}
        utils={utils}
        options={{
            ...options,
            buttons: getButtons(),
            loading: loading,
            paging: {
                data: paging,
                limit: limit,
                offset: offset.current,
                onClick: next => {
                    offset.current = next;
                    fetchEventTypes();
                }
            },
            search: {
                placeholder: 'Search by event type name...',
                onChange: setSearchText
            }
        }}>
            <div style={{
                ...Appearance.styles.unstyledPanel(),
                width: '100%'
            }}>
                {getContent()}
            </div>
        </Panel>
    )
}

export const getEventStatus = (utils, event, options = {}) => {

    // prevent moving forward if no event status was provided
    if(!event || !event.status) {
        return null;
    }

    // prepare status click handler
    const onStatusClick = evt => {

        // prevent root component from triggering click event
        evt.stopPropagation();

        // only allow editing if options object does not contain a false editing flag
        if(options.editable !== false) {
            utils.layer.open({
                abstract: Abstract.create({
                    object: event,
                    type: 'events'
                }),
                Component: SetEventStatus,
                id: `set_event_status_${event.id}`,
                permissions: ['events.actions.edit']
            });
        }
    }

    return (
        <div
        className={options.editable === false ? '' : 'text-button'}
        onClick={onStatusClick}
        style={{
            alignItems: 'center',
            background: Appearance.colors.softGradient(event.status.color),
            border: `1px solid ${event.status.color}`,
            borderRadius: 5,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            justifyContent: 'center',
            maxWidth: 95,
            overflow: 'hidden',
            paddingLeft: 8,
            paddingRight: 8,
            textAlign: 'center',
            width: '100%'
        }}>
            <span style={{
                ...Appearance.textStyles.subTitle(),
                color: 'white',
                fontWeight: '600',
                width: '100%'
            }}>{event.status.text}</span>
        </div>
    )
}

export const LeadScriptDetails = ({ abstract, index, options, utils }) => {

    const layerID = `lead_script_details_${abstract.getID()}`;
    const [client, setClient] = useState(abstract.object.client);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);

    const getButtons = () => {
        return [{
            color: 'secondary',
            key: 'options',
            onClick: onOptionsClick,
            text: 'Options'
        },{
            color: 'primary',
            key: 'edit',
            onClick: onEditClick,
            text: 'Edit'
        }];
    }

    const getFields = () => {

        let script = abstract.object;
        let items = [{
            key: 'details',
            title: 'Details',
            items: [{
                key: 'client',
                title: 'Client',
                visible: client && utils.user.get().level < User.levels.get().primary ? true : false,
                value: client ? client.name : null
            },{
                key: 'is_default',
                title: 'Deafult for Client',
                visible: client ? true : false,
                value: script.is_default ? 'Yes' : 'No'
            },{
                key: 'id',
                title: 'ID',
                value: script.id
            },{
                key: 'active',
                title: 'Status',
                value: script.active ? 'Active' : 'Inactive'
            },{
                key: 'title',
                title: 'Title',
                value: script.title
            },]
        }]

        return items;
    }

    const onEditClick = () => {

        // prevent editing if script is company-wide and user is not an admin
        if(!abstract.object.client_id && utils.user.get().level > User.levels.get().admin) {
            utils.alert.show({
                title: 'Just a Second',
                message: 'This lead script is available to everyone in the LifeWipe network and can only be modified by the support. Please create a new lead script if you need to change the information in this lead script.'
            });
            return;
        }

        // open layer for script editing
        utils.layer.open({
            abstract: abstract,
            Component: AddEditLeadScript.bind(this, {
                isNewTarget: false
            }),
            id: `edit_lead_script_${abstract.getID()}`,
            permissions: ['leads.scripts.actions.edit']
        });
    }

    const onOptionsClick = evt => {
        utils.sheet.show({
            items: [{
                key: 'active',
                permissions: ['leads.scripts.actions.status'],
                title: abstract.object.active ? 'Deactivate' : 'Activate',
                style: abstract.object.active ? 'destructive' : 'default'
            },{
                key: 'default_status',
                permissions: ['leads.scripts.actions.default'],
                title: 'Set as Client Default',
                visible: abstract.object.is_default !== true,
                style: 'default'
            }],
            target: evt.target
        }, key => {
            if(key === 'active') {
                onChangeActiveStatus();
                return;
            }
            if(key === 'default_status') {
                onSetDefaultStatus();
                return;
            }
        });
    }

    const onChangeActiveStatus = () => {

        // prevent editing if script is company-wide and user is not an admin
        if(!abstract.object.client_id && utils.user.get().level > User.levels.get().admin) {
            utils.alert.show({
                title: 'Just a Second',
                message: 'This lead script is available to everyone in the LifeWipe network and can only be modified by the support. Please create a new lead script if you need to change the information in this lead script.'
            });
            return;
        }

        // request confirmation to change lead script status
        utils.alert.show({
            title: `${abstract.object.active ? 'Deactivate' : 'Activate'} Lead Script`,
            message: `Are you sure that you want to ${abstract.object.active ? 'deactivate' : 'activate'} this lead script?`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: abstract.object.active ? 'destructive' : 'default'
            },{
                key: 'cancel',
                title: `Do Not ${abstract.object.active ? 'Deactivate' : 'Activate'}`,
                style: abstract.object.active ? 'default' : 'destructive'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onChangeActiveStatusConfirm();
                    return;
                }
            }
        });
    }

    const onChangeActiveStatusConfirm = async () => {
        try {
            setLoading(true);
            let next_status = !abstract.object.active;
            await Request.post(utils, '/clients/', {
                type: 'set_lead_script_active_status',
                id: abstract.getID(),
                active: next_status
            });

            abstract.object.active = next_status;
            utils.content.fetch(abstract);

            setLoading(false);
            utils.banner.show({
                title: 'Script Status Changed',
                message: `This script has been set as ${next_status ? 'active' : 'inactive'}`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue updating the active status for this script. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const onSetDefaultStatus = () => {
        if(!abstract.object.client_id && utils.user.get().level > User.levels.get().admin) {
            utils.alert.show({
                title: 'Just a Second',
                message: 'This lead script is available to everyone in the LifeWipe network and can only be modified by the support. Please create a new lead script if you need to change the information in this lead script.'
            });
            return;
        }
        utils.alert.show({
            title: `Set as Default for Client`,
            message: `Are you sure that you want to set this lead script as the default lead script? This will automatically attach this script when a new Lead is created.`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onSetDefaultStatusConfirm();
                    return;
                }
            }
        });
    }

    const onSetDefaultStatusConfirm = async () => {
        try {
            setLoading(true);
            await Utils.sleep(0.25);

            await Request.post(utils, '/clients/', {
                type: 'set_default_lead_script',
                id: abstract.getID()
            });

            setLoading(false);
            abstract.object.is_default = true;
            utils.content.update(abstract);

            utils.content.fetch('lead_scripts');
            utils.banner.show({
                title: 'Default Script Set',
                message: `This script has been set as the default lead script for ${utils.client.get().name}`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue updating the default status for this script. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const fetchClient = async () => {
        try {
            // globally offered lead scripts do not have a client
            if(!abstract.object.client_id) {
                return;
            }

            if(!abstract.object.client) {

                // attach local client if a match is found
                let client = utils.client.get();
                if(abstract.object.client_id === client.id) {
                    setClient(client);
                    return;
                }

                // fetch client details
                let nextClient = await Client.get(utils, abstract.object.client_id);
                abstract.object.client = nextClient;
                setClient(nextClient);
            }
        } catch(e) {
            console.error(e.message);
        }
    }

    useEffect(() => {
        fetchClient();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={abstract.getTitle()}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading,
            sizing: 'medium'
        }}>
            <LayerItem
            collapsed={false}
            title={'Content Preview'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <div style={{
                        color: Appearance.colors.text(),
                        fontSize: config.fontSize,
                        fontWeight: 500,
                        maxHeight: 300,
                        overflowY: 'scroll',
                        padding: '8px 12px 8px 12px',
                    }}>
                        {formatLeadScriptContent({
                            editable: true,
                            text: abstract.object.text,
                            utils: utils
                        })}
                    </div>
                </div>
            </LayerItem>
            <FieldMapper 
            sections={getFields()} 
            utils={utils} />
        </Layer>
    )
}

export const LeadScriptsList = ({ index, options, utils }) => {

    const panelID = 'lead_scripts_list';
    const limit = 15;

    const [loading, setLoading] = useState(null);
    const [offset, setOffset] = useState(0);
    const [paging, setPaging] = useState(null);
    const [searchText, setSearchText] = useState(null);
    const [scripts, setScripts] = useState([]);

    const onNewScript = () => {
        utils.layer.open({
            abstract: Abstract.create({
                object: Lead.Script.new(),
                type: 'lead_scripts'
            }),
            Component: AddEditLeadScript.bind(this, {
                isNewTarget: true
            }),
            id: 'new_lead_script',
            permissions: ['leads.scripts.actions.new']
        });
    }

    const onPrintScripts = async props => {
        return new Promise(async (resolve, reject) => {
            try {
                setLoading(true);
                let { scripts } = await Request.get(utils, '/clients/', {
                    type: searchText ? 'lookup' : 'all',
                    search_text: searchText,
                    ...props
                });

                setLoading(false);
                resolve(scripts.map(script => Lead.Script.create(script)));

            } catch(e) {
                setLoading(false);
                reject(e);
            }
        })
    }

    const onScriptClick = script => {
        utils.layer.open({
            abstract: Abstract.create({
                object: script,
                type: 'lead_scripts'
            }),
            Component: LeadScriptDetails,
            id: `lead_script_details_${script.id}`,
            permissions: ['leads.scripts.details']
        })
    }

    const getPrintProps = () => {
        return {
            onFetch: onPrintScripts,
            onRenderItem: item => ({
                title: item.title,
                text: item.text
            }),
            headers: [{
                key: 'title',
                title: 'Title'
            },{
                key: 'text',
                title: 'Script'
            }]
        }
    }

    const getBadges = script => {
        let badges = [];
        if(script.is_default === true) {
            badges.push({
                text: 'Client Default',
                color: Appearance.colors.primary()
            })
        }
        if(script.active === false) {
            badges.push({
                text: 'Not Active',
                color: Appearance.colors.grey()
            })
        }
        return badges;
    }

    const getContent = () => {
        if(scripts.length === 0) {
            return Views.entry({
                bottomBorder: false,
                hideIcon: true,
                subTitle: 'No scripts were found in the system',
                title: 'No Scripts Found'
            });
        }
        return scripts.map((script, index) => {
            return (
                Views.entry({
                    badge: getBadges(script),
                    bottomBorder: index !== scripts.length - 1,
                    icon: {
                        path: 'images/lead-script-icon-green.png'
                    },
                    key: index,
                    onClick: onScriptClick.bind(this, script),
                    subTitle: `Created ${moment(script.date).format('MMMM Do, YYYY [at] h:mma')}`,
                    title: script.title
                })
            )
        });
                
    }

    const fetchScripts = async () => {
        try {
            setLoading(true);
            let { lead_scripts, paging } = await Request.get(utils, '/clients/', {
                limit: limit,
                offset: offset,
                search_text: searchText,
                type: 'lead_scripts'
            });

            setLoading(false);
            setPaging(paging);
            setScripts(lead_scripts.map(script => Lead.Script.create(script)));

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the lead scripts list. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    useEffect(() => {
        fetchScripts();
    }, [searchText, offset]);

    useEffect(() => {
        utils.events.on(panelID, 'client_change', fetchScripts);
        utils.content.subscribe(panelID, ['lead_scripts'], {
            onFetch: fetchScripts,
            onUpdate: abstract => {
                setScripts(scripts => {
                    if(!scripts) {
                        return scripts;
                    }
                    return scripts.map(script => {
                        return script.id === abstract.getID() ? abstract.object : script;
                    })
                })
            }
        })
        return () => {
            utils.events.off(panelID, 'client_change');
            utils.content.unsubscribe(panelID);
        }

    }, []);

    return (
        <Panel
        index={index}
        title={'Scripts'}
        utils={utils}
        options={{
            ...options,
            buttons: [{
                key: 'new',
                onClick: onNewScript,
                title: 'New Script',
                style: 'default',
            }],
            loading: loading,
            paging: {
                data: paging,
                limit: limit,
                offset: offset,
                onClick: nextOffset => setOffset(nextOffset)
            },
            print: getPrintProps(),
            search: {
                onChange: setSearchText,
                placeholder: 'Search by script title or content...'
            }
        }}>
            <div style={{
                ...Appearance.styles.unstyledPanel(),
                width: '100%'
            }}>
                {getContent()}
            </div>
        </Panel>
    )
}

export const LeadSubTypesList = ({ index, options, utils }) => {

    const panelID = 'lead_sub_types_list';
    const limit = 15;
    const offset = useRef(0);

    const [loading, setLoading] = useState(null);
    const [paging, setPaging] = useState(null);
    const [searchText, setSearchText] = useState(null);
    const [leadSubTypes, setLeadSubTypes] = useState([]);

    const onChangeActiveStatus = async (leadSubType, e) => {
        try {
            e.stopPropagation();
            let status = !leadSubType.active;
            await Request.post(utils, '/clients/', {
                active: status,
                id: leadSubType.id,
                type: 'set_lead_sub_type_active_status'
            });

            setLeadSubTypes(types => {
                return types.map(prev => {
                    if(prev.id === leadSubType.id) {
                        prev.active = status;
                    }
                    return prev;
                })
            });

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue changing the status of this lead sub-type. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onDelete = leadSubType => {
        utils.alert.show({
            title: 'Delete Lead Sub-Type',
            message: `Are you sure that you want to delete this lead sub-type? This can not be undone.`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onDeleteConfirm(leadSubType);
                    return;
                }
            }
        });
    }

    const onDeleteConfirm = async leadSubType => {
        try {
            setLoading(true);
            await Request.post(utils, '/clients/', {
                id: leadSubType.id,
                type: 'delete_lead_sub_type'
            });

            setLoading(false);
            utils.content.fetch('lead_sub_types');
            utils.banner.show({
                title: 'Sub-Type Deleted',
                message: `The "${leadSubType.text}" has been removed from your client workspace.`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue deleting this lead sub-type from your client workspace. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onEdit = leadSubType => {
        
        let title = leadSubType.title;
        utils.alert.show({
            title: 'Editing Lead Sub-Type',
            message: 'What would you like to call this lead sub-type?',
            content: (
                <div style={{
                    paddingBottom: 16,
                    paddingLeft: 16,
                    paddingRight: 16,
                    width: '100%'
                }}>
                    <TextField
                    fieldStyle={{
                        textAlign: 'center'
                    }}
                    placeholder={leadSubType.title}
                    onChange={text => title = text} />
                </div>
            ),
            buttons: [{
                key: 'save',
                title: 'Save',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'save' && title) {
                    onUpdateLeadSubType(leadSubType, title);
                    return;
                }
            }
        });
    }

    const onLeadSubTypeClick = leadSubType => {
        utils.sheet.show({
            items: [{
                key: 'edit',
                permissions: ['leads.sub_types.actions.edit'],
                title: 'Edit',
                style: 'default'
            },{
                key: 'delete',
                permissions: ['leads.sub_types.actions.delete'],
                title: 'Delete',
                style: 'destructive'
            }]
        }, key => {
            switch(key) {
                case 'delete':
                onDelete(leadSubType);
                break;

                case 'edit':
                onEdit(leadSubType);
                break;
            }
        });
    }

    const onNewLeadSubType = () => {

        // prevent moving forward if feature is disabled for current user
        if(utils.user.permissions.get('leads.sub_types.actions.new') === false) {
            return utils.user.permissions.reject();
        }

        // show alert requesting lead sub-type information
        let title = null;
        utils.alert.show({
            title: 'New Lead Sub-Type',
            message: 'What would you like to call this lead sub-type?',
            content: (
                <div style={{
                    paddingBottom: 16,
                    paddingLeft: 16,
                    paddingRight: 16,
                    width: '100%'
                }}>
                    <TextField
                    fieldStyle={{
                        textAlign: 'center'
                    }}
                    placeholder={'Name'}
                    onChange={text => title = text} />
                </div>
            ),
            buttons: [{
                key: 'save',
                title: 'Save',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'save' && title) {
                    onNewLeadSubTypeConfirm(title);
                    return;
                }
            }
        });
    }

    const onNewLeadSubTypeConfirm = async title => {
        try {

            // set loading flag and sleep briefly so previous alert can close
            setLoading(true);
            await Utils.sleep(0.25);

            // send request to server
            await Request.post(utils, '/clients/', {
                text: title,
                type: 'new_lead_sub_type'
            });

            // end loading and show confirmation alert
            setLoading(false);
            utils.content.fetch('lead_sub_types');
            utils.banner.show({
                title: 'Sub-Type Created',
                message: `The "${title}" lead sub-type has been created and added to your list of lead sub-types`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue creating your lead sub-type. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onUpdateLeadSubType = async (leadSubType, title) => {
        try {

            // set loading flag and sleep briefly so previous alert can close
            setLoading(true);
            await Utils.sleep(0.25);

            // send request to server
            await Request.post(utils, '/clients/', {
                id: leadSubType.id,
                text: title,
                type: 'update_lead_sub_type'
            });

            // notify subscribers of data change
            leadSubType.text = title;
            utils.content.fetch('lead_sub_types');

            // end loading and show confirmation alert
            setLoading(false);
            utils.banner.show({
                title: 'Sub-Type Updated',
                message: `The "${title}" lead sub-type has been updated`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue updating your lead sub-type. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const getActiveStatus = leadType => {
        if(!leadType) {
            return;
        }
        if(!leadType.client_id && utils.user.get().level > User.levels.get().admin) {
            return null;
        }
        let color = leadType.active ? Appearance.colors.primary() : Appearance.colors.grey();
        return (
            <div
            className={'text-button'}
            onClick={onChangeActiveStatus.bind(this, leadType)}
            style={{
                alignItems: 'center',
                background: Appearance.colors.softGradient(color),
                border: `1px solid ${color}`,
                borderRadius: 5,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                justifyContent: 'center',
                maxWidth: 75,
                overflow: 'hidden',
                textAlign: 'center',
                width: 100
            }}>
                <span style={{
                    ...Appearance.textStyles.subTitle(),
                    color: 'white',
                    fontWeight: '600',
                    width: '100%'
                }}>{leadType.active ? 'Active' : 'Not Active'}</span>
            </div>
        )
    }

    const getContent = () => {
        if(leadSubTypes.length === 0) {
            return Views.entry({
                title: 'No Lead Types Found',
                subTitle: 'No lead types were found in the system',
                bottomBorder: false,
                hideIcon: true
            });
        }
        return leadSubTypes.map((leadSubType, index) => {
            return (
                Views.entry({
                    badge: {
                        color: Appearance.colors.grey(),
                        text: leadSubType.is_default ? 'Default' : null
                    },
                    bottomBorder: index !== leadSubTypes.length - 1,
                    hideIcon: true,
                    key: index,
                    onClick: onLeadSubTypeClick.bind(this, leadSubType),
                    rightContent: getActiveStatus(leadSubType),
                    subTitle: `Created ${moment(leadSubType.date).format('MMMM Do, YYYY [at] h:mma')}`,
                    title: leadSubType.text
                })
            )
        })
    }

    const fetchLeadSubTypes = async () => {
        try {
            setLoading(true);
            let { lead_sub_types, paging } = await Request.get(utils, '/clients/', {
                limit: limit,
                offset: offset.current,
                search_text: searchText,
                show_inactive: true,
                type: 'lead_sub_types'
            });

            setLoading(false);
            setPaging(paging);
            setLeadSubTypes(lead_sub_types.map(type => Lead.SubType.create(type)));

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the lead sub-types list. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    useEffect(() => {
        fetchLeadSubTypes();
    }, [searchText]);

    useEffect(() => {
        utils.events.on(panelID, 'client_change', fetchLeadSubTypes);
        utils.content.subscribe(panelID, ['lead_sub_types'], {
            onFetch: fetchLeadSubTypes,
            onUpdate: abstract => {
                setLeadSubTypes(leadSubTypes => {
                    return leadSubTypes.map(leadSubType => {
                        return leadSubType.id === abstract.getID() ? abstract.object : leadSubType;
                    });
                });
            }
        });
        return () => {
            utils.events.off(panelID, 'client_change');
            utils.content.unsubscribe(panelID);
        }

    }, []);

    return (
        <Panel
        index={index}
        title={'Lead Sub-Types'}
        utils={utils}
        options={{
            ...options,
            buttons: [{
                key: 'new',
                title: 'New Lead Sub-Type',
                style: 'default',
                onClick: onNewLeadSubType
            }],
            loading: loading,
            paging: {
                data: paging,
                limit: limit,
                offset: offset.current,
                onClick: props => {
                    offset.current = props;
                    fetchLeadSubTypes();
                }
            },
            search: {
                placeholder: 'Search by lead sub-type name...',
                onChange: setSearchText
            }
        }}>
            <div style={{
                ...Appearance.styles.unstyledPanel(),
                width: '100%'
            }}>
                {getContent()}
            </div>
        </Panel>
    )
}

export const LeadTypeDetails = ({ abstract, index, options, utils }) => {

    const layerID = `lead_type_details_${abstract.getID()}`;
    const [client, setClient] = useState(abstract.object.client);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);

    const onChangeActiveStatus = () => {

        // prevent moving forward if the lead type is globally offered and the current user is not an administrator
        if(!abstract.object.client_id && utils.user.get().level > User.levels.get().admin) {
            utils.alert.show({
                title: 'Just a Second',
                message: 'This lead type is available to everyone in the LifeWipe network and can only be modified by the support. Please create a new lead type if you need to change the information in this lead type.'
            });
            return;
        }

        // request confirmation to change lead type status
        utils.alert.show({
            title: `${abstract.object.active ? 'Deactivate' : 'Activate'} Lead Type`,
            message: `Are you sure that you want to ${abstract.object.active ? 'deactivate' : 'activate'} this lead type?`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: abstract.object.active ? 'destructive' : 'default'
            },{
                key: 'cancel',
                title: `Do Not ${abstract.object.active ? 'Deactivate' : 'Activate'}`,
                style: abstract.object.active ? 'default' : 'destructive'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onChangeActiveStatusConfirm();
                    return;
                }
            }
        });
    }

    const onChangeActiveStatusConfirm = async () => {
        try {
            setLoading(true);
            await Utils.sleep(0.25);

            let next_status = !abstract.object.active;
            await Request.post(utils, '/clients/', {
                type: 'set_lead_type_active_status',
                id: abstract.getID(),
                active: next_status
            });

            abstract.object.active = next_status;
            utils.content.update(abstract);

            setLoading(false);
            utils.banner.show({
                title: 'Lead Type Status Changed',
                message: `This lead type has been set as ${next_status ? 'active' : 'inactive'}`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue updating the active status for this lead type. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const onDeleteClick = () => {
        utils.alert.show({
            title: 'Delete Lead Type',
            message: 'Are you sure that you want to delete this lead type? This can not be undone.',
            buttons: [{
                key: 'confirm',
                title: 'Delete',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onDeleteLeadTypeConfirm();
                    return;
                }
            }
        })
    }

    const onDeleteLeadTypeConfirm = async () => {
        try {
            setLoading(true);
            await Utils.sleep(0.25);
            await Request.post(utils, '/clients/', {
                type: 'delete_lead_type',
                id: abstract.getID()
            });

            setLoading(false);
            utils.content.fetch('lead_types');
            utils.banner.show({
                title: 'Lead Type Deleted',
                message: `The "${abstract.object.text}" lead type has been deleted`,
                onShow: () => setLayerState('close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue deleting this lead type. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const onEditClick = () => {

        // prevent moving forward if the lead type is globally offered and the current user is not an administrator
        if(!abstract.object.client_id && utils.user.get().level > User.levels.get().admin) {
            utils.alert.show({
                title: 'Just a Second',
                message: 'This lead type is available to everyone in the LifeWipe network and can only be modified by the support. Please create a new lead type if you need to change the information in this lead type.'
            });
            return;
        }

        // open editing layer for lead type
        utils.layer.open({
            abstract: abstract,
            Component: AddEditLeadType.bind(this, {
                isNewTarget: false
            }),
            id: `edit_lead_type_${abstract.getID()}`,
            permissions: ['leads.types.actions.edit']
        })
    }

    const onOptionsClick = evt => {
        utils.sheet.show({
            items: [{
                key: 'active',
                permissions: ['leads.types.actions.status'],
                title: abstract.object.active ? 'Deactivate' : 'Activate',
                style: abstract.object.active ? 'destructive' : 'default'
            },{
                key: 'delete',
                permissions: ['leads.types.actions.delete'],
                title: 'Delete from Client',
                style: 'destructive'
            },{
                key: 'default_status',
                permissions: ['leads.types.actions.default'],
                title: 'Set as Client Default',
                visible: abstract.object.is_default !== true,
                style: 'default'
            }],
            target: evt.target
        }, key => {
            if(key === 'active') {
                onChangeActiveStatus();
                return;
            }
            if(key === 'default_status') {
                onSetDefaultStatus();
                return;
            }
            if(key === 'delete') {
                onDeleteClick();
                return;
            }
        })
    }

    const onScriptClick = () => {
        utils.layer.open({
            abstract: Abstract.create({
                object: abstract.object.lead_script,
                type: 'lead_scripts'
            }),
            Component: LeadScriptDetails,
            id: `lead_script_details_${abstract.object.lead_script.id}`,
            permissions: ['leads.scripts.details']
        });
    }

    const onSetDefaultStatus = () => {

        // prevent moving forward if the lead type is globally offered and the current user is not an administrator
        if(!abstract.object.client_id && utils.user.get().level > User.levels.get().admin) {
            utils.alert.show({
                title: 'Just a Second',
                message: 'This lead type is available to everyone in the LifeWipe network and can only be modified by the support. Please create a new lead type if you need to change the information in this lead type.'
            });
            return;
        }

        // request confirmation to set lead type as the client default
        utils.alert.show({
            title: `Set as Default`,
            message: `Are you sure that you want to set this lead type as the default lead type? This will automatically attach this lead type when a new lead is created.`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onSetDefaultStatusConfirm();
                    return;
                }
            }
        });
    }

    const onSetDefaultStatusConfirm = async () => {
        try {
            setLoading(true);
            await Utils.sleep(0.25);

            await Request.post(utils, '/clients/', {
                type: 'set_default_lead_type',
                id: abstract.getID()
            });

            setLoading(false);
            abstract.object.is_default = true;
            utils.content.update(abstract);

            utils.content.fetch('lead_types');
            utils.banner.show({
                title: 'Default Lead Type Set',
                message: `This lead type has been set as the default lead type for ${utils.client.get().name}`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue updating the default status for this lead type. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const getButtons = () => {
        return [{
            color: 'secondary',
            key: 'options',
            onClick: onOptionsClick,
            text: 'Options'
        },{
            color: 'primary',
            key: 'edit',
            onClick: onEditClick,
            permissions: ['leads.types.actions.edit'],
            text: 'Edit'
        }];
    }

    const getFields = () => {

        let leadType = abstract.object;
        let items = [{
            key: 'details',
            title: 'Details',
            items: [{
                key: 'client',
                title: 'Client',
                visible: client && utils.user.get().level < User.levels.get().primary ? true : false,
                value: client ? client.name : null
            },{
                key: 'is_default',
                title: 'Deafult for Client',
                visible: client ? true : false,
                value: leadType.is_default ? 'Yes' : 'No'
            },{
                key: 'icon',
                title: 'Icon',
                visible: leadType.icon ? true : false,
                value: leadType.icon && (
                    <img 
                    src={leadType.icon.url} 
                    style={{
                        borderRadius: 10,
                        height: 20,
                        objectFit: 'cover',
                        width: 20 
                    }}/>
                )
            },{
                key: 'id',
                title: 'ID',
                value: leadType.id
            },{
                key: 'lead_script',
                title: 'Lead Script',
                ...leadType.lead_script && {
                    value: leadType.lead_script.title,
                    onClick: onScriptClick
                }
            },{
                key: 'text',
                title: 'Name',
                value: leadType.text
            },{
                key: 'active',
                title: 'Status',
                value: leadType.active ? 'Active' : 'Inactive'
            }]
        }]

        return items;
    }

    const fetchClient = async () => {
        try {
            // globally offered lead scripts do not have a client
            if(!abstract.object.client_id) {
                return;
            }

            if(!abstract.object.client) {

                // attach local client if a match is found
                let client = utils.client.get();
                if(abstract.object.client_id === client.id) {
                    setClient(client);
                    return;
                }

                // fetch client details
                let nextClient = await Client.get(utils, abstract.object.client_id);
                abstract.object.client = nextClient;
                setClient(nextClient);
            }
        } catch(e) {
            console.error(e.message);
        }
    }

    useEffect(() => {
        fetchClient();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={abstract.getTitle()}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading,
            sizing: 'medium'
        }}>
            <FieldMapper
            sections={getFields()} 
            utils={utils}/>
        </Layer>
    )
}

export const LeadTypesList = ({ index, options, utils }) => {

    const panelID = 'lead_types_list';
    const limit = 15;
    const offset = useRef(0);

    const [loading, setLoading] = useState(null);
    const [paging, setPaging] = useState(null);
    const [searchText, setSearchText] = useState(null);
    const [leadTypes, setLeadTypes] = useState([]);

    const onChangeActiveStatus = async (leadType, e) => {
        try {
            e.stopPropagation();
            let status = !leadType.active;
            await Request.post(utils, '/clients/', {
                type: 'set_lead_type_active_status',
                id: leadType.id,
                active: status
            });

            setLeadTypes(leadTypes => {
                return leadTypes.map(prevLeadType => {
                    if(prevLeadType.id === leadType.id) {
                        prevLeadType.active = status;
                    }
                    return prevLeadType;
                })
            })

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue changing the status of this lead type. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const onLeadTypeClick = leadType => {
        utils.layer.open({
            abstract: Abstract.create({
                object: leadType,
                type: 'lead_types'
            }),
            Component: LeadTypeDetails,
            id: `lead_type_details_${leadType.id}`,
            permissions: ['leads.types.details']
        });
    }

    const onNewLeadType = () => {
        utils.layer.open({
            abstract: Abstract.create({
                object: Lead.Type.new(),
                type: 'lead_types'
            }),
            Component: AddEditLeadType.bind(this,{
                isNewTarget: true
            }),
            id: 'new_lead_type',
            permissions: ['leads.types.actions.new']
        })
    }

    const getActiveStatus = leadType => {
        if(!leadType) {
            return;
        }
        if(!leadType.client_id && utils.user.get().level > User.levels.get().admin) {
            return null;
        }
        let color = leadType.active ? Appearance.colors.primary() : Appearance.colors.grey();
        return (
            <div
            className={'text-button'}
            onClick={onChangeActiveStatus.bind(this, leadType)}
            style={{
                alignItems: 'center',
                background: Appearance.colors.softGradient(color),
                border: `1px solid ${color}`,
                borderRadius: 5,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                justifyContent: 'center',
                maxWidth: 75,
                overflow: 'hidden',
                textAlign: 'center',
                width: 100,
            }}>
                <span style={{
                    ...Appearance.textStyles.subTitle(),
                    color: 'white',
                    fontWeight: '600',
                    width: '100%'
                }}>{leadType.active ? 'Active' : 'Not Active'}</span>
            </div>
        )
    }

    const getButtons = () => {
        return [{
            key: 'new',
            onClick: onNewLeadType,
            style: 'default',
            title: 'New Lead Type'
        }];
    }

    const getContent = () => {
        if(leadTypes.length === 0) {
            return Views.entry({
                title: 'No Lead Types Found',
                subTitle: 'No lead types were found in the system',
                bottomBorder: false,
                hideIcon: true
            })
        }
        return leadTypes.map((leadType, index) => {
            return (
                Views.entry({
                    badge: [{
                        color: Appearance.colors.grey(),
                        text: leadType.is_default ? 'Default' : null
                    }],
                    bottomBorder: index !== leadTypes.length - 1,
                    icon: {
                        path: leadType.icon.url,
                        imageStyle: {
                            backgroundColor: Appearance.colors.transparent
                        }
                    },
                    key: index,
                    onClick: onLeadTypeClick.bind(this, leadType),
                    rightContent: getActiveStatus(leadType),
                    subTitle: `Created ${moment(leadType.date).format('MMMM Do, YYYY [at] h:mma')}`,
                    title: leadType.text
                })
            )
        })
    }

    const fetchLeadTypes = async () => {
        try {
            setLoading(true);
            let { lead_types, paging } = await Request.get(utils, '/clients/', {
                limit: limit,
                offset: offset.current,
                search_text: searchText,
                show_inactive: true,
                type: 'lead_types'
            });

            setLoading(false);
            setPaging(paging);
            setLeadTypes(lead_types.map(type => Lead.Type.create(type)));

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the lead types list. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    useEffect(() => {
        fetchLeadTypes();
    }, [searchText]);

    useEffect(() => {
        utils.events.on(panelID, 'client_change', fetchLeadTypes);
        utils.content.subscribe(panelID, ['lead_types'], {
            onFetch: fetchLeadTypes,
            onUpdate: abstract => {
                setLeadTypes(leadTypes => {
                    return leadTypes.map(leadType => {
                        return leadType.id === abstract.getID() ? abstract.object : leadType;
                    });
                });
            }
        });
        return () => {
            utils.events.off(panelID, 'client_change');
            utils.content.unsubscribe(panelID);
        }

    }, []);

    return (
        <Panel
        index={index}
        title={'Lead Types'}
        utils={utils}
        options={{
            ...options,
            buttons: getButtons(),
            loading: loading,
            search: {
                placeholder: 'Search by lead type name...',
                onChange: setSearchText
            },
            paging: {
                data: paging,
                limit: limit,
                offset: offset,
                onClick: next => {
                    offset.current = next;
                    fetchLeadTypes();
                }
            }
        }}>
            <div style={{
                ...Appearance.styles.unstyledPanel(),
                width: '100%'
            }}>
                {getContent()}
            </div>
        </Panel>
    )
}

export const ManageAppointmentSlots = ({ onChange, slots }, { index, options, utils }) => {

    const layerID = 'manage_appointment_slots';
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [targets, setTargets] = useState(slots || []);

    const onDeleteSlotClick = slot => {
        utils.alert.show({
            title: 'Delete Appointment Slot',
            message: 'Are you sure that you want to delete this appointment slot? This can not be undone.',
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onDeleteSlotConfirm(slot);
                    return;
                }
            }
        });
    }

    const onDeleteSlotConfirm = async slot => {
        try {

            // set loading flag and submit request to server
            setLoading(true);
            await Request.post(utils, '/clients/', {
                id: slot.id,
                type: 'delete_appointment_slot'
            });

            // filter requested slot out of targets list and update local state
            let next = targets.filter(target => target.id !== slot.id);
            setTargets(next);

            // end loading and notify subscribers of data change
            setLoading(false);
            if(typeof(onChange) === 'function') {
                onChange(next);
            }

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue deleting this appointment slot. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onEditSlotClick = slot => {
        utils.layer.open({
            abstract: Abstract.create({
                object: slot,
                type: 'appointment_slot'
            }),
            id: `edit_appointment_slot_${slot.id}`,
            Component: AddEditAppointmentSlot.bind(this, {
                isNewTarget: false
            })
        });
    }

    const onNewAppointmentSlotClick = () => {
        utils.layer.open({
            abstract: Abstract.create({
                object: Appointment.Slot.new(),
                type: 'appointment_slot'
            }),
            Component: AddEditAppointmentSlot.bind(this, {
                isNewTarget: true,
                onChange: result => {

                    // add new slot to local state of targets
                    let next = update(targets, {$push: [result]});
                    setTargets(next);

                    // notify subscribers that data has changed
                    if(typeof(onChange) === 'fucntion') {
                        onChange(next);
                    }
                }
            }),
            id: 'new_appointment_slot'
        });
    }

    const getButtons = () => {
        return [{
            color: 'primary',
            key: 'done',
            onClick: onNewAppointmentSlotClick,
            text: 'New Appointment Slot'
        }];
    }

    const getSlots = () => {
        return (
            <div style={{
                ...Appearance.styles.unstyledPanel(),
                width: '100%'
            }}>
                {targets.length === 0 && (
                    Views.entry({
                        bottomBorder: false,
                        hideIcon: true,
                        subTitle: 'No appointment slots were found for this client',
                        title: 'No Slots Found'
                    })
                )}
                {targets.sort((a,b) => {
                    return a.name.localeCompare(b.name);
                }).map((slot, index) => {
                    return (
                        Views.entry({
                            bottomBorder: index !== targets.length - 1,
                            hideIcon: true,
                            key: index,
                            rightContent: (
                                <div style={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    flexDirection: 'row'
                                }}>
                                    <img
                                    className={'text-button'}
                                    onClick={onEditSlotClick.bind(this, slot)}
                                    title={'Edit'}
                                    src={'images/edit-button-yellow-small.png'}
                                    style={{
                                        height: 25,
                                        marginLeft: 8,
                                        objectFit: 'contain',
                                        width: 25
                                    }} />
                                    <img
                                    className={'text-button'}
                                    onClick={onDeleteSlotClick.bind(this, slot)}
                                    title={'Delete'}
                                    src={'images/red-x-icon.png'}
                                    style={{
                                        height: 25,
                                        marginLeft: 8,
                                        objectFit: 'contain',
                                        width: 25
                                    }} />
                                </div>
                            ),
                            subTitle: `${formatHour(slot.start_hour)} to ${formatHour(slot.end_hour)}`,
                            title: slot.name
                        })
                    )
                })}
            </div>
        );
    }

    const formatHour = val => {
        let hour = val > 12 ? val - 12 : val;
        return `${hour === 0 ? 12 : hour}:00 ${val > 11 ? 'PM' : 'AM'}`;
    }
    
    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={'Manage Appointment Slots'}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading,
            sizing: 'medium'
        }}>
            {getSlots()}
        </Layer>
    )
}

export const ManageHiddenSurveyMonkeySurveys = ({ client, onChange }, { index, options, utils }) => {

    const layerID = `manage_hidden_survey_monkey_surveys_${client.id}`;
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState('init');
    const [surveys, setSurveys] = useState([]);

    const onSurveyClick = survey => {
        utils.sheet.show({
            items: [{
                key: 'add',
                title: 'Add Back to LifeWipe',
                style: 'default'
            }]
        }, key => {
            if(key === 'add') {
                onAddSurveyBackToAccount(survey.id);
                return;
            }
        });
    }

    const onAddSurveyBackToAccount = async id => {
        utils.alert.show({
            title: 'Add Back to LifeWipe',
            message: 'Are you sure that you want to add this survey back to your LifeWipe account? This survey will become available for automatic lead generation.',
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    setTimeout(onAddSurveyBackToAccountConfirm.bind(this, id), 250);
                    return;
                }
            }
        });
    }

    const onAddSurveyBackToAccountConfirm = async id => {
        try {

            // send request to server
            setLoading(true);
            await Request.post(utils, '/survey_monkey/', {
                id: id,
                type: 'undelete_survey'
            });

            // notify subscribers of data change
            if(typeof(onChange) === 'function') {
                onChange();
            }

            // end loading and show confirmation alert
            setLoading(false);
            utils.banner.show({
                title: 'Survey Added',
                message: 'This survey has been added back to your LifeWipe account',
                onShow: () => {
                    if(surveys.length === 1) {
                        setLayerState('close');
                    }
                }
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue adding this survey back to your LifeWipe account. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const getContent = () => {

        // determine if a loading component is needed
        if(loading === 'init') {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    padding: 15,
                    width: '100%'
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 50,
                        width: 50
                    }}/>
                </div>
            )
        }
        
        return surveys.sort((a,b) => {
            return a.title.localeCompare(b.title);
        }).map((survey, index) => {
            return (
                Views.entry({
                    bottomBorder: index !== surveys.length - 1,
                    hideIcon: true,
                    key: index,
                    onClick: onSurveyClick.bind(this, survey),
                    title: survey.title
                })
            )
        });
    }

    const fetchSurveys = async () => {
        try {
            
            let { surveys } = await Request.get(utils, '/survey_monkey/', {
                client_id: client.id,
                type: 'hidden_surveys'
            });

            // determine if there were no hidden surveys found
            if(surveys.length === 0) {
                utils.alert.show({
                    title: 'No Surveys Found',
                    message: 'There were no deleted surveys found for your Survey Monkey account.',
                    onClick: setLayerState.bind(this, 'close')
                });
                return;
            }

            // end loading and update local state
            setLoading(false);
            setSurveys(surveys);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the list of deleted surveys. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    useEffect(() => {
        fetchSurveys();
    }, []);

    return (
        <Layer
        id={layerID}
        index={index}
        title={'Manage Deleted Survey Monkey Surveys'}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            sizing: 'medium'
        }}>
            <div style={{
                ...Appearance.styles.unstyledPanel(),
                width: '100%'
            }}>
                {getContent()}
            </div>
        </Layer>
    )
}

export const SetEventStatus = ({ abstract, index, options, utils }) => {

    const layerID = `set_event_status_${abstract.getID()}`;
    const [items, setItems] = useState([]);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState(null);

    const onSetStatus = async () => {
        try {
            setLoading('done');
            await Utils.sleep(0.25);

            // submit request to the server
            let { status } = await Request.post(utils, '/events/', {
                id: abstract.getID(),
                status: selectedStatus,
                type: 'set_status'
            })

            // end loading and update abstract target
            setLoading(false);
            abstract.object.status = status;

            // notify subscribers that target has been updated
            utils.content.update(abstract);

            // show user confirmation alert
            utils.banner.show({
                title: 'Status Updated',
                message: `The status for this ${abstract.object.type.title} has been updated`,
                onShow: setLayerState.bind(this, 'close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue updating the status for this ${abstract.object.type.title}. ${e.message || 'An unknown error occurred'}`,
            })
        }
    }

    const getButtons = () => {
        return [{
            color: selectedStatus ? 'primary' : 'dark',
            key: 'done',
            loading: loading === 'done',
            onClick: onSetStatus,
            text: 'Save Changes'
        }];
    }

    const setupStatusCodes = () => {
        let codes = utils.client.status_codes.get().filter(status => {
            return status.capabilities.events;
        }).map(status => ({
            id: status.code,
            title: status.text
        }));
        setItems(codes);
    }

    useEffect(() => {
        setupStatusCodes();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={`Set ${abstract.object.type.title} Status`}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            sizing: 'small'
        }}>
            <select
            className={`custom-select ${window.theme}`}
            defaultValue={'Choose a status...'}
            onChange={e => {
                let id = Utils.attributeForKey.select(e, 'id');
                setSelectedStatus(parseInt(id));
            }}
            style={{
                background: `${Appearance.colors.textField()} url("images/custom-select-list-arrows-${window.theme === 'dark' ? 'white' : 'black'}.png") no-repeat right 0.75rem center`,
                backgroundSize: '10px',
                width: '100%'
            }}>
                <option disabled={true}>{'Choose a status...'}</option>
                {items.map((item, index) => {
                    return (
                        <option key={index} id={item.id}>{item.title}</option>
                    )
                })}
            </select>
        </Layer>
    )
}

export const StatusCodesList = ({ index, options, utils }) => {

    const panelID = 'status_codes_list';
    const limit = 15;
    const offset = useRef(0);

    const [loading, setLoading] = useState(null);
    const [paging, setPaging] = useState(null);
    const [searchText, setSearchText] = useState(null);
    const [statusCodes, setStatusCodes] = useState([]);

    const onChangeActiveStatus = async (statusCode, evt) => {
        try {
            evt.stopPropagation();
            let status = !statusCode.active;
            await Request.post(utils, '/clients/', {
                active: status,
                id: statusCode.id,
                type: 'set_status_active_status'
            });

            setStatusCodes(statusCodes => {
                return statusCodes.map(prev => {
                    if(prev.id === statusCode.id) {
                        prev.active = status;
                    }
                    return prev;
                });
            });

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue changing the active status for this status code. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onNewStatusCode = () => {

        // open editing layer for new status
        utils.layer.open({
            id: 'new_status',
            abstract: Abstract.create({
                type: 'status',
                object: Status.new()
            }),
            Component: AddEditStatus.bind(this,{
                isNewTarget: true
            })
        });
    }

    const onStatusCodeClick = status => {
        utils.layer.open({
            id: `status_details_${status.code}`,
            abstract: Abstract.create({
                object: status,
                type: 'status'
            }),
            Component: StatusDetails
        });
    }

    const getContent = () => {
        if(statusCodes.length === 0) {
            return Views.entry({
                bottomBorder: false,
                hideIcon: true,
                subTitle: 'No custom lead status codes were found in the system',
                title: 'No Status Codes Found'
            });
        }
        return statusCodes.map((statusCode, index) => {
            return (
                Views.entry({
                    bottomBorder: index !== statusCodes.length - 1,
                    key: index,
                    onClick: onStatusCodeClick.bind(this, statusCode),
                    rightContent: (
                        <div
                        className={'text-button'}
                        onClick={onChangeActiveStatus.bind(this, statusCode)}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 100,
                            height: '100%',
                            maxWidth: 75,
                            textAlign: 'center',
                            border: `1px solid ${statusCode.active ? Appearance.colors.primary() : Appearance.colors.grey()}`,
                            background: Appearance.colors.softGradient(statusCode.active ? Appearance.colors.primary() : Appearance.colors.grey()),
                            borderRadius: 5,
                            overflow: 'hidden'
                        }}>
                            <span style={{
                                ...Appearance.textStyles.subTitle(),
                                color: 'white',
                                fontWeight: '600',
                                width: '100%'
                            }}>{statusCode.active ? 'Active' : 'Not Active'}</span>
                        </div>
                    ),
                    subTitle: `Created ${moment(statusCode.date).format('MMMM Do, YYYY [at] h:mma')}`,
                    title: statusCode.text,
                    icon: {
                        path: 'images/custom-status-code-clear.png',
                        imageStyle: {
                            backgroundColor: statusCode.color
                        }
                    }
                })
            )
        })
    }

    const fetchStatusCodes = async () => {
        try {
            setLoading(true);
            let { status_codes, paging } = await Request.get(utils, '/clients/', {
                limit: limit,
                offset: offset.current,
                search_text: searchText,
                show_inactive: true,
                type: 'status_codes'
            });

            setLoading(false);
            setPaging(paging);
            setStatusCodes(status_codes.map(status => Status.create(status)));

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the status codes list. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    useEffect(() => {
        fetchStatusCodes();
    }, [searchText]);

    useEffect(() => {
        utils.events.on(panelID, 'client_change', fetchStatusCodes);
        utils.content.subscribe(panelID, ['status'], {
            onFetch: fetchStatusCodes,
            onUpdate: abstract => {
                setStatusCodes(statusCodes => {
                    return statusCodes.map(statusCode => {
                        return statusCode.id === abstract.getID() ? abstract.object : statusCode;
                    })
                });
            }
        })
        return () => {
            utils.events.off(panelID, 'client_change');
            utils.content.unsubscribe(panelID);
        }

    }, []);
    const getButtons = () => {
        return [{
            key: 'new',
            onClick: onNewStatusCode,
            style: 'default',
            title: 'New Status'
        }];
    }

    return (
        <Panel
        index={index}
        title={'Status Codes'}
        utils={utils}
        options={{
            ...options,
            buttons: getButtons(),
            loading: loading,
            paging: {
                data: paging,
                limit: limit,
                offset: offset.current,
                onClick: next => {
                    offset.current = next;
                    fetchStatusCodes();
                }
            },
            search: {
                placeholder: 'Search by status name...',
                onChange: setSearchText
            }
        }}>
            <div style={{
                ...Appearance.styles.unstyledPanel(),
                width: '100%'
            }}>
                {getContent()}
            </div>
        </Panel>
    )
}

export const StatusDetails = ({ abstract, index, options, utils }) => {

    const layerID = `status_details_${abstract.object.code}`;
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);

    const onChangeActiveStatus = () => {
        utils.alert.show({
            title: `${abstract.object.active ? 'Deactivate' : 'Activate'} Status`,
            message: `Are you sure that you want to ${abstract.object.active ? 'deactivate' : 'activate'} the "${abstract.object.text}" status?`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: abstract.object.active ? 'destructive' : 'default'
            },{
                key: 'cancel',
                title: `Do Not ${abstract.object.active ? 'Deactivate' : 'Activate'}`,
                style: abstract.object.active ? 'default' : 'destructive'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    setTimeout(onChangeActiveStatusConfirm, 250);
                    return;
                }
            }
        });
    }

    const onChangeActiveStatusConfirm = async () => {
        try {

            // submit request to server
            setLoading(true);
            let next_status = !abstract.object.active;
            await Request.post(utils, '/clients/', {
                active: next_status,
                id: abstract.getID(),
                type: 'update_active_flag_for_status'
            });

            // update local active flag for status
            abstract.object.active = next_status;
            utils.content.update(abstract);

            setLoading(false);
            utils.banner.show({
                title: 'Status Changed',
                message: `This status has been ${next_status ? 'activated' : 'deactivated'}.`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue updating the active status for this status code. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onDeleteClick = () => {
        utils.alert.show({
            title: 'Delete Status',
            message: 'Are you sure that you want to delete this status? This can not be undone.',
            buttons: [{
                key: 'confirm',
                title: 'Delete',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    setTimeout(onDeleteStatusConfirm, 250);
                    return;
                }
            }
        })
    }

    const onDeleteStatusConfirm = async () => {
        try {

            // send request to server
            setLoading(true);
            await Request.post(utils, '/clients/', {
                id: abstract.getID(),
                type: 'delete_status'
            });

            // end loading and notify subscribers that new data is available
            setLoading(false);
            utils.content.fetch('status');

            // show confirmation alert
            utils.banner.show({
                title: 'Status Deleted',
                message: `The "${abstract.object.text}" status has been deleted`,
                onShow: setLayerState.bind(this, 'close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue deleting this status. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onEditClick = () => {

        // prevent moving forward if status is global and user is not an administrator
        if(!abstract.object.client_id && utils.user.get().level > User.levels.get().admin) {
            utils.alert.show({
                title: 'Just a Second',
                message: 'This status is offered to all client in the LifeWipe ecosystem and can not be modified. Please create a new status if you would like to have additional status options.'
            });
            return;
        }

        // show editing layer
        utils.layer.open({
            id: `edit_status_${abstract.getID()}`,
            abstract: abstract,
            Component: AddEditStatus.bind(this, {
                isNewTarget: false
            })
        });
    }

    const onOptionsClick = evt => {
        utils.sheet.show({
            items: [{
                key: 'active',
                title: abstract.object.active ? 'Deactivate' : 'Activate',
                style: abstract.object.active ? 'destructive' : 'default'
            },{
                key: 'delete',
                title: 'Delete from Client',
                style: 'destructive'
            }],
            target: evt.target
        }, key => {
            if(key === 'active') {
                onChangeActiveStatus();
                return;
            }
            if(key === 'delete') {
                onDeleteClick();
                return;
            }
        })
    }

    const getButtons = () => {
        return [{
            color: 'secondary',
            key: 'options',
            onClick: onOptionsClick,
            text: 'Options'
        },{
            color: 'primary',
            key: 'edit',
            onClick: onEditClick,
            text: 'Edit'
        }];
    }

    const getFields = () => {
        return [{
            key: 'details',
            title: 'Details',
            items: [{
                color: abstract.object.color,
                key: 'color',
                title: 'Color',
                value: abstract.object.color && abstract.object.color.toUpperCase()
            },{
                key: 'id',
                title: 'ID',
                value: abstract.object.id
            },{
                key: 'text',
                title: 'Name',
                value: abstract.object.text
            },{
                color: abstract.object.active ? Appearance.colors.green : Appearance.colors.red,
                key: 'active',
                title: 'Status',
                value: abstract.object.active ? 'Active' : 'Inactive'
            }]
        },{
            key: 'capabilities',
            title: 'Capabilities',
            items: Object.keys(abstract.object.capabilities).map(key => ({
                key: key,
                title: `Available for ${Utils.ucFirst(key)}`,
                value: abstract.object.capabilities[key] ? 'Yes' : 'No'
            }))
        }];
    }

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={abstract.object.text}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading,
            sizing: 'medium'
        }}>
            <FieldMapper
            sections={getFields()}
            utils={utils} />
        </Layer>
    )
}

export const SurveyMonkeyPreferences = ({ index, options, utils }) => {
    
    const layerID = 'survey_monkey_preferences';
    const [layerState, setLayerState] = useState(false);
    const [loading, setLoading] = useState(false);
    const [preferences, setPreferences] = useState({});

    const onConfigurationClick = () => {

        // prevent moving forward if feature is disabled for current user
        if(utils.user.permissions.get('surveys.survey_monkey.actions.edit') === false) {
            return utils.user.permissions.reject();
        }

        // show alert with options to configure the global data plugin 
        utils.alert.show({
            title: 'Configure Survey Monkey',
            message: `To start receiving leads from Survey Monkey, you'll need to install the LifeWipe Survey Monkey plugin with your Survey Monkey account.`,
            buttons: [{
                key: 'confirm',
                title: 'Get Started',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    window.open('https://www.surveymonkey.com/apps/1LQG7O63KaHnGcT61M8_2BKg_3D_3D/details/');
                    return;
                }
            }
        });
    }

    const onEnabledStatusClick = () => {

        // prevent moving forward if feature is disabled for current user
        if(utils.user.permissions.get('surveys.survey_monkey.actions.edit') === false) {
            return utils.user.permissions.reject();
        }

        // show sheet with options to manage survey monkey integration status
        utils.sheet.show({
            items: [{
                key: 'status',
                title: `${preferences.enabled ? 'Disconnect from' : 'Connect to'} Survey Monkey`,
                style: preferences.enabled ? 'destructive' : 'default'
            }]
        }, key => {
            switch(key) {
                case 'status':
                onSetEnabledStatus();
                break;
            }
        });
    }

    const onManageHiddenSurveyMonkeySurveys = () => {
        let client = utils.client.get();
        utils.layer.open({
            Component: ManageHiddenSurveyMonkeySurveys.bind(this, {
                client: client,
                onChange: fetchPreferences
            }),
            id: `manage_hidden_survey_monkey_surveys_${client.id}`,
        });
    }

    const onOptionsClick = evt => {
        utils.sheet.show({
            items: [{
                key: 'hidden_surveys',
                permissions: ['surveys.survey_monkey.actions.edit'],
                title: 'Manage Deleted Surveys',
                style: 'default'
            }],
            target: evt.target
        }, key => {
            switch(key) {
                case 'hidden_surveys':
                onManageHiddenSurveyMonkeySurveys();
                break;
            }
        });
    }

    const onSetEnabledStatus = () => {
        utils.alert.show({
            title: `${preferences.enabled ? 'Disconnect From' : 'Connect To'} Survey Monkey`,
            message: `Are you sure that you want to ${preferences.enabled ? 'disconnect from' : 'connect to'} Survey Monkey? ${preferences.enabled ? 'This means that your Survey Monkey account will no longer communicate with your LifeWipe account.' : 'This means that your Survey Monkey account will begin to communicate with your LifeWipe account.'}`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: preferences.enabled ? 'destructive' : 'default'
            },{
                key: 'cancel',
                title: `Do Not ${preferences.enabled ? 'Disconnect' : 'Connect'}`,
                style: preferences.enabled ? 'default' : 'destructive'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    setTimeout(onSetEnabledStatusConfirm, 250);
                    return;
                }
            }
        });
    }

    const onSetEnabledStatusConfirm = async () => {
        try {

            // declare next enabled status
            let status = preferences.enabled ? false : true;

            // send request to server
            setLoading('status');
            await Request.post(utils, '/survey_monkey/', {
                status: status,
                type: 'set_enabled_status'
            });

            // update survey monkey api integration status
            setPreferences(prefs => {
                prefs.enabled = status;
                return prefs;
            });
            
            // end loading and show confirmation alert
            setLoading(false);
            utils.banner.show({
                title: 'Account Connected',
                message: `Your Survey Monkey account has been ${status ? 'connected to' : 'disconnected from'} your LifeWipe account.`,
                onShow: () => {
                    if(status === false) {
                        setLayerState('close');
                    }
                }
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue ${preferences.enabled ? 'disconnecting from' : 'connecting to'}. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onSurveyClick = survey => {
        utils.layer.open({
            id: `survey_monkey_survey_details_${survey.id}`,
            Component: SurveyMonkeySurveyDetails.bind(this, {
                onChange: setPreferences,
                preferences: preferences,
                survey: survey
            })
        });
    }

    const getButtons = () => {
        return [{
            color: 'secondary',
            key: 'options',
            loading: loading === 'options',
            onClick: onOptionsClick,
            permissions: ['surveys.survey_monkey.actions.edit'],
            text: 'Options'
        }];
    }

    const getPreferences = () => {

        // determine if a loading component is needed
        if(loading === true) {
            return (
                <LayerItem title={'Preferences'}>
                    <div style={Appearance.styles.unstyledPanel()}>
                        <div style={{
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            padding: 15
                        }}>
                            <LottieView
                            autoPlay={true}
                            loop={true}
                            source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                            style={{
                                height: 50,
                                width: 50
                            }}/>
                        </div>
                    </div>
                </LayerItem>
            )
        }

        // prepare preferences fields
        let fields = [{
            key: 'preferences',
            title: 'Preferences',
            items: [{
                color: Appearance.colors.orange,
                key: 'token',
                loading: loading === 'status',
                onClick: onConfigurationClick,
                title: 'Survey Monkey',
                value: 'Not Configured',
                visible: preferences.token ? false : true
            },{
                color: preferences.enabled ? Appearance.colors.green : Appearance.colors.red ,
                key: 'enabled',
                loading: loading === 'status',
                onClick: onEnabledStatusClick,
                title: 'Survey Monkey',
                value: preferences.enabled ? 'Connected' : 'Disconnected',
                visible: preferences.token ? true : false
            }]
        }];

        return (
            <FieldMapper
            sections={fields}
            utils={utils} />
        )
    }

    const getSurveys = () => {

        // determine if a loading component is needed
        if(loading === true) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    padding: 15
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 50,
                        width: 50
                    }}/>
                </div>
            )
        }

        // show a placeholder if no surveys have been synced
        let surveys = preferences && preferences.surveys || [];
        if(surveys.length === 0) {
            return (
                Views.entry({
                    bottomBorder: false,
                    hideIcon: true,
                    subTitle: 'We did not find any surveys linked with your LifeWipe account',
                    title: 'No Surveys Found'
                })
            )
        }
        return surveys.sort((a,b) => {
            return a.title.localeCompare(b.title);
        }).map((survey, index) => {
            return (
                Views.entry({
                    ...getSurveySubTitleProps(survey),
                    bottomBorder: index !== surveys.length - 1,
                    hideIcon: true,
                    key: index,
                    onClick: onSurveyClick.bind(this, survey),
                    title: survey.title
                })
            )
        });
    }

    const getSurveySubTitleProps = survey => {

        // determine if lead generation has been disabled for the survey
        if(survey.enabled === false) {
            return {
                subTitle: 'Lead Generation Disabled',
                style: {
                    subTitle: {
                        color: Appearance.colors.red
                    }
                }
            }
        }

        // determine if a lead map has not yet been generated for this survey
        if(!preferences.lead_maps[survey.id]) {
            return {
                subTitle: 'Additional Configuration Required',
                style: {
                    subTitle: {
                        color: Appearance.colors.orange
                    }
                }
            }
        }

        // fallback to successful entry for a configured survey
        return {
            subTitle: 'Lead Generation Enabled',
            style: {
                subTitle: {
                    color: Appearance.colors.green
                }
            }
        }
    }

    const fetchPreferences = async () => {
        try {
            setLoading(true);
            let { preferences } = await Request.get(utils, '/survey_monkey/', {
                client_id: utils.client.get().id,
                type: 'preferences'
            });
            setLoading(false);
            setPreferences(preferences);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the survey monkey preferences. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    useEffect(() => {
        // TODO => add websockets listener for app_install and app_uninstall changes
        fetchPreferences();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={'Survey Monkey Preferences'}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            sizing: 'medium'
        }}>
            {getPreferences()}
            <LayerItem title={'Surveys'}>
                <div style={Appearance.styles.unstyledPanel()}>
                    {getSurveys()}
                </div>
            </LayerItem>
        </Layer>
    )
}

export const SurveyMonkeySurveyDetails = ({ onChange, preferences, survey }, { index, options, utils }) => {
    
    const layerID = `survey_monkey_survey_details_${survey.id}`;
    const [collectors, setCollectors] = useState([]);
    const [data, setData] = useState(null);
    const [defaultValues, setDefaultValues] = useState({});
    const [edits, setEdits] = useState(null);
    const [items, setItems] = useState([]);
    const [layerState, setLayerState] = useState(null);
    const [leadTypes, setLeadTypes] = useState([]);
    const [leadSubTypes, setLeadSubTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);

    const onCollectorValueChange = (collector, user) => {
        setEdits(edits => {
            if(!edits.collectors[collector.id]) {
                edits.collectors[collector.id] = {};
            }
            return update(edits, {
                collectors: {
                    [collector.id]: {
                        value: {
                            $set: user && user.user_id
                        }
                    }
                }
            });
        });
    }

    const onCreateNewCustomValue = async (question, title) => {
        try {

            // set loading and submit request to server
            setLoading('custom_value');
            await Utils.sleep(0.5);
            let { id } = await Request.post(utils, '/survey_monkey/', {
                title: title,
                type: 'add_custom_lead_map_value'
            });

            // end loading and update items list
            setLoading(false);
            setItems(values => {
                return values.concat([{ 
                    custom: true,
                    id: id,
                    title: title 
                }]).sort((a,b) => {
                    return a.title.localeCompare(b.title);
                })
            });

            // update selection for question with new value
            setEdits(edits => {
                if(!edits.lead_map[question.id]) {
                    edits.lead_map[question.id] = {};
                }
                return update(edits, {
                    lead_map: {
                        [question.id]: {
                            value: {
                                $set: id
                            }
                        }
                    }
                })
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue creating your custom value. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onDeleteSurvey = () => {
        utils.alert.show({
            title: 'Delete Survey from LifeWipe',
            message: 'Are you sure that you want to delete this survey from LifeWipe? This survey will no longer be available for automatic lead generation.',
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    setTimeout(onDeleteSurveyConfirm, 250);
                    return;
                }
            }
        });
    }

    const onDeleteSurveyConfirm = async () => {
        try {

            // send request to server
            setLoading('options');
            await Request.post(utils, '/survey_monkey/', {
                id: survey.id,
                type: 'delete_survey'
            });

            // remove survey from local list of surveys
            preferences.surveys = preferences.surveys.filter(s => s.id !== survey.id);
            
            // notify subscribers of data change
            if(typeof(onChange) === 'function') {
                onChange(preferences);
            }

            // end loading and show confirmation alert
            setLoading(false);
            utils.banner.show({
                title: 'Survey Removed',
                message: 'This survey has been removed from LifeWipe',
                onShow: setLayerState.bind(this, 'close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue deleting this survey from LifeWipe. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onLeadValueChange = (question, item) => {

        // request a column name from the user if applicable
        if(item && item.id === 'custom_value') {
            let title = null;
            utils.alert.show({
                title: 'Custom Value',
                message: 'What would you like to call this custom value?',
                content: (
                    <div style={{
                        paddingBottom: 12,
                        paddingLeft: 12,
                        paddingRight: 12,
                        width: '100%'
                    }}>
                        <TextField
                        placeholder={'Value Name'}
                        onChange={text => title = text}/>
                    </div>
                ),
                buttons: [{
                    key: 'confirm',
                    title: 'Done',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(key === 'confirm' && title) {
                        onCreateNewCustomValue(question, title);
                        return;
                    }
                }
            });
            return;
        }
        
        // update edits with new selection for question
        setEdits(edits => {
            if(!edits.lead_map[question.id]) {
                edits.lead_map[question.id] = {};
            }
            return update(edits, {
                lead_map: {
                    [question.id]: {
                        custom: {
                            $set: item.custom || false
                        },
                        value: {
                            $set: item && item.id
                        }
                    }
                }
            })
        });
    }

    const onOptionsClick = evt => {
        utils.sheet.show({
            items: [{
                key: 'delete',
                permissions: ['surveys.survey_monkey.actions.edit'],
                title: 'Delete Survey from LifeWipe',
                style: 'destructive'
            },{
                key: 'enabled',
                permissions: ['surveys.survey_monkey.actions.edit'],
                title: `${survey.enabled ? 'Disable' : 'Enable'} Lead Generation`,
                style: survey.enabled ? 'destructive' : 'default'
            }],
            target: evt.target
        }, key => {
            switch(key) {
                case 'delete':
                onDeleteSurvey();
                break;

                case 'enabled':
                onSetEnabledStatus();
                break;
            }
        });
    }

    const onSetEnabledStatus = () => {
        utils.alert.show({
            title: `${survey.enabled ? 'Disable' : 'Enable'} Lead Generation`,
            message: `Are you sure that you want to ${survey.enabled ? 'disable' : 'enable'} lead generation? ${survey.enabled ? 'This means that leads will no longer be generated when a new survey monkey response is submitted.' : 'This means that a lead will be automatically created in LifeWipe when a new survey monkey response is submitted using this survey.'}`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: survey.enabled ? 'destructive' : 'default'
            },{
                key: 'cancel',
                title: `Do Not ${survey.enabled ? 'Disable' : 'Enable'}`,
                style: survey.enabled ? 'default' : 'destructive'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    setTimeout(onSetEnabledStatusConfirm, 250);
                    return;
                }
            }
        });
    }

    const onSetEnabledStatusConfirm = async () => {
        try {

            // declare next enabled status
            let status = survey.enabled ? false : true;

            // send request to server
            setLoading('options');
            await Request.post(utils, '/survey_monkey/', {
                id: survey.id,
                status: status,
                type: 'set_survey_enabled_status'
            });

            // remove survey from local list of surveys
            preferences.surveys = preferences.surveys.map(s => {
                if(s.id === survey.id) {
                    s.enabled = status;
                }
                return s;
            });
            
            // notify subscribers of data change
            if(typeof(onChange) === 'function') {
                onChange(preferences);
            }

            // end loading and show confirmation alert
            setLoading(false);
            utils.banner.show({
                title: 'Lead Generation Changed',
                message: `Lead generation for this survey has been ${status ? 'enabled' : 'disabled'}`,
                onShow: () => {
                    if(status === false) {
                        setLayerState('close');
                    }
                }
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue updating the lead generation status for this survey. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onSubmit = async () => {
        try {
            setLoading('submit');
            await Request.post(utils, '/survey_monkey/', {
                collectors: edits.collectors,
                default_values: defaultValues,
                id: survey.id,
                map: edits.lead_map,
                type: 'update_survey_preferences'
            });

            // notify subscribers that data has changed
            if(typeof(onChange) === 'function') {
                onChange({ 
                    ...preferences, 
                    collectors: update(preferences.collectors || {}, {
                        [survey.id]: {
                            $set: edits.collectors
                        }
                    }),
                    lead_maps: update(preferences.lead_maps || {}, {
                        [survey.id]: {
                            $set: edits.lead_map
                        }
                    })
                });
            }

            // end loading and show confirmation alert
            setLoading(false);
            utils.banner.show({
                title: 'Preferences Saved',
                message: 'The changes to your survey preferences have been saved'
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue submitting your survey preferences. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const getButtons = () => {
        return [{
            color: 'secondary',
            key: 'options',
            loading: loading === 'options',
            onClick: onOptionsClick,
            text: 'Options'
        },{
            color: 'primary',
            key: 'submit',
            loading: loading === 'submit',
            onClick: onSubmit,
            permissions: ['surveys.survey_monkey.actions.edit'],
            text: 'Submit'
        }];
    }

    const getContent = () => {
        if(!data || loading === true) {
            return (
                <div style={{
                    padding: 12
                }}>
                    <div style={Appearance.styles.unstyledPanel()}>
                        <div style={{
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            padding: 15
                        }}>
                            <LottieView
                            autoPlay={true}
                            loop={true}
                            source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                            style={{
                                height: 50,
                                width: 50
                            }}/>
                        </div>
                    </div>
                </div>
            )
        }

        // prepare lead map items and generate question inputs
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <div style={{
                    borderBottom: `1px solid ${Appearance.colors.divider()}`,
                    padding: 12,
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.subHeader(),
                        display: 'block'
                    }}>{'Survey Configuration'}</span>
                    <span style={{
                        ...Appearance.textStyles.subTitle(),
                        whiteSpace: 'normal'
                    }}>{`Below you'll find a list of the questions from your survey. Review each question and determine if you would like the answer to be used when generating a new lead from a survey response.`}</span>
                </div>
                {getQuestionsSection()}
                {getSurveyCreditSection()}
                {getDefaultValuesSection()}
            </div>
        )
    }

    const getDefaultValuesSection = () => {

        // prepare fields for default values section
        let fields = [{
            key: 'lead_type',
            onChange: item => setDefaultValues(props => ({ ...props, lead_type: item && item.id })),
            onLoad: setLeadTypes,
            title: 'Lead Type',
            value: getValueForType('lead_type', leadTypes)
        },{
            key: 'lead_sub_type',
            onChange: item => setDefaultValues(props => ({ ...props, lead_sub_type: item && item.id })),
            onLoad: setLeadSubTypes,
            title: 'Lead Sub-Type',
            value: getValueForType('lead_sub_type', leadSubTypes)
        }];

        return (
            <>
            <div style={{
                padding: 12,
                width: '100%'
            }}>
                <span style={{
                    ...Appearance.textStyles.subHeader(),
                    display: 'block'
                }}>{'Preferences'}</span>
                <span style={{
                    ...Appearance.textStyles.subTitle(),
                    whiteSpace: 'normal'
                }}>{`Below you can customize how leads are entered into your client workspace when a customer provides their information using this survey.`}</span>
            </div>
            <div style={{
                padding: 12,
                width: '100%'
            }}>
                {fields.map((field, index) => {

                    // determine which input component to show
                    let component = null;
                    switch(field.key) {
                        case 'lead_type':
                        component = (
                            <LeadTypePickerField
                            {...field}
                            utils={utils} />
                        )
                        break;

                        case 'lead_sub_type':
                        component = (
                            <LeadSubTypePickerField
                            {...field}
                            utils={utils} />
                        )
                        break;
                    }

                    return (
                        <div 
                        key={index}
                        style={{
                            ...Appearance.styles.unstyledPanel(),
                            marginBottom: 12
                        }}>
                            <div style={{
                                backgroundColor: Appearance.colors.divider(),
                                padding: '8px 12px 8px 12px',
                                width: '100%'
                            }}>
                                <span style={{
                                    ...Appearance.textStyles.title(),
                                    display: 'block',
                                    maxWidth: '100%',
                                    whiteSpace: 'wrap',
                                    width: '100%'
                                }}>{field.title}</span>
                            </div>
                            <div style={{
                                borderTop: `1px solid ${Appearance.colors.divider()}`,
                                display: 'flex',
                                padding: '8px 12px 8px 12px',
                                width: '100%'
                            }}>
                                {component}
                            </div>
                        </div>
                    )
                })}
            </div>
            </>
        )
    }

    const getLeadMappingPreferences = (question, index) => {

        // determine if nested rows are available
        if(question.rows) {
            return (
                <div 
                key={index}
                style={{
                    padding: 12,
                    width: '100%'
                }}>
                    {question.rows.map(row => {
                        let key = `${index}.${row.position}`;
                        return (
                            <div 
                            key={key}
                            style={{
                                ...Appearance.styles.unstyledPanel(),
                                marginBottom: 12
                            }}>
                                <div style={{
                                    backgroundColor: Appearance.colors.divider(),
                                    padding: '8px 12px 8px 12px',
                                    width: '100%'
                                }}>
                                    <span style={{
                                        ...Appearance.textStyles.title(),
                                        display: 'block',
                                        maxWidth: '100%',
                                        whiteSpace: 'wrap',
                                        width: '100%'
                                    }}>{`${index + 1}.${row.position} ${row.title}`}</span>
                                </div>
                                {getLeadMappingPreferences(row, key)}
                            </div>
                        )
                    })}
                </div>
            )
        }

        // fallback to rendering default components
        let { enabled, value } = edits.lead_map[question.id] || {};
        return (
            <div 
            key={index}
            style={{
                width: '100%'
            }}>
                <div style={{
                    alignItems: 'center',
                    borderTop: `1px solid ${Appearance.colors.divider()}`,
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    padding: '8px 12px 8px 12px',
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.key()
                    }}>{'Use for Lead Generation'}</span>
                    <ListField
                    disablePlaceholder={true}
                    items={[{
                        id: 'yes',
                        title: 'Yes'
                    },{
                        id: 'no',
                        title: 'No'
                    }]}
                    onChange={item => {
                        setEdits(edits => {
                            if(!edits.lead_map[question.id]) {
                                edits.lead_map[question.id] = {};
                            }
                            return update(edits, {
                                lead_map: {
                                    [question.id]: {
                                        enabled: {
                                            $set: item && item.id === 'yes'
                                        }
                                    }
                                }
                            })
                        });
                    }} 
                    style={{
                        maxWidth: 225
                    }}
                    value={enabled === true ? 'Yes' : 'No'}/>
                </div>
                {enabled === true && (
                    <div style={{
                        alignItems: 'center',
                        borderTop: `1px solid ${Appearance.colors.divider()}`,
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        padding: '8px 12px 8px 12px',
                        width: '100%'
                    }}>
                        <span style={{
                            ...Appearance.textStyles.key()
                        }}>{'Use Question Response For'}</span>
                        <ListField
                        disablePlaceholder={true}
                        items={items}
                        placeholder={'Choose a lead value...'}
                        onChange={onLeadValueChange.bind(this, question)} 
                        style={{
                            maxWidth: 225
                        }}
                        value={value && items.find(item => item.id === value).title}/>
                    </div>
                )}
            </div>
        )
    }

    const getQuestionsSection = () => {
        return (
            <div style={{
                borderBottom: `1px solid ${Appearance.colors.divider()}`,
                padding: 12,
                width: '100%'
            }}>
                {data.pages.map(page => {
                    return page.questions.map((question, index) => {
                        return (
                            <div 
                            key={index}
                            style={{
                                ...Appearance.styles.unstyledPanel(),
                                marginBottom: 12
                            }}>
                                <div style={{
                                    backgroundColor: Appearance.colors.divider(),
                                    padding: '8px 12px 8px 12px',
                                    width: '100%'
                                }}>
                                    <span style={{
                                        ...Appearance.textStyles.title(),
                                        display: 'block',
                                        maxWidth: '100%',
                                        whiteSpace: 'wrap',
                                        width: '100%'
                                    }}>{`${index + 1}. ${question.title}`}</span>
                                </div>
                                {getLeadMappingPreferences(question, index)}
                            </div>
                        )
                    });
                })}
            </div>
        )
    }

    const getSurveyCreditSection = () => {
        return (
            <div style={{
                borderBottom: `1px solid ${Appearance.colors.divider()}`,
                width: '100%'
            }}>
                <div style={{
                    borderBottom: `1px solid ${Appearance.colors.divider()}`,
                    padding: 12,
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.subHeader(),
                        display: 'block'
                    }}>{'Survey Credit'}</span>
                    <span style={{
                        ...Appearance.textStyles.subTitle(),
                        whiteSpace: 'normal'
                    }}>{`You can assign survey credit to a user in your client workspace based on the collector label that you have setup in Survey Monkey. Choose a user from the list next to the collector to make an association.`}</span>
                </div>
                <div style={{
                    padding: 12,
                    width: '100%'
                }}>
                    {collectors.map((collector, index) => {
                        let { enabled, value } = edits.collectors[collector.id] || {};
                        return (
                            <div 
                            key={index}
                            style={{
                                ...Appearance.styles.unstyledPanel(),
                                marginBottom: 12
                            }}>
                                <div style={{
                                    backgroundColor: Appearance.colors.divider(),
                                    padding: '8px 12px 8px 12px',
                                    width: '100%'
                                }}>
                                    <span style={{
                                        ...Appearance.textStyles.title(),
                                        display: 'block',
                                        maxWidth: '100%',
                                        whiteSpace: 'wrap',
                                        width: '100%'
                                    }}>{collector.title}</span>
                                </div>
                                <div style={{
                                    alignItems: 'center',
                                    borderTop: `1px solid ${Appearance.colors.divider()}`,
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                    padding: '8px 12px 8px 12px',
                                    width: '100%'
                                }}>
                                    <span style={{
                                        ...Appearance.textStyles.key()
                                    }}>{'Use for Survey Credit'}</span>
                                    <ListField
                                    disablePlaceholder={true}
                                    items={[{
                                        id: 'yes',
                                        title: 'Yes'
                                    },{
                                        id: 'no',
                                        title: 'No'
                                    }]}
                                    onChange={item => {
                                        setEdits(edits => {
                                            if(!edits.collectors[collector.id]) {
                                                edits.collectors[collector.id] = {};
                                            }
                                            return update(edits, {
                                                collectors: {
                                                    [collector.id]: {
                                                        enabled: {
                                                            $set: item && item.id === 'yes'
                                                        }
                                                    }
                                                }
                                            })
                                        });
                                    }} 
                                    style={{
                                        maxWidth: 225
                                    }}
                                    value={enabled === true ? 'Yes' : 'No'}/>
                                </div>
                                {enabled === true && (
                                    <div style={{
                                        alignItems: 'center',
                                        borderTop: `1px solid ${Appearance.colors.divider()}`,
                                        display: 'flex',
                                        flexDirection: 'row',
                                        justifyContent: 'space-between',
                                        padding: '8px 12px 8px 12px',
                                        width: '100%'
                                    }}>
                                        <span style={{
                                            ...Appearance.textStyles.key()
                                        }}>{'Assign Survey Credit To'}</span>
                                        <UserLookupField
                                        containerStyle={{
                                            maxWidth: 225
                                        }}
                                        hideAvatar={true}
                                        onChange={onCollectorValueChange.bind(this, collector)} 
                                        utils={utils}
                                        user={value && users.find(user => user.user_id === value)}/>
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            </div>
        )
    }

    const getValueForType = (key, targets) => {
        let match = defaultValues[key] && targets.find(type => type.id === defaultValues[key]);
        return match && match.text;
    }

    const fetchDetails = async () => {
        try {

            // fetch lead columns and details for survey from server
            setLoading(true);
            let response = await Request.get(utils, '/survey_monkey/', {
                custom_lead_map_values: true,
                id: survey.id,
                type: 'survey_details'
            });
            console.log(response);

            // end loading and set survey details
            setLoading(false);
            setCollectors(response.collectors.map(collector => ({
                id: collector.id,
                title: collector.name
            })));
            setData(response.survey); 
            setDefaultValues(response.default_values || {});
            setUsers(response.users);

            // prepare list of default lead columns for mapping
            let targets = [{
                id: 'custom_value',
                title: '* Custom Value *'
            },{
                id: 'locality',
                title: 'City'
            },{
                id: 'date',
                title: 'Date Created'
            },{
                id: 'email_address',
                title: 'Email Address'
            },{
                id: 'first_name',
                title: 'First Name'
            },{
                id: 'last_name',
                title: 'Last Name'
            },{
                id: 'lead_credit_user',
                title: 'Lead Credit'
            },{
                id: 'lead_script',
                title: 'Lead Script'
            },{
                id: 'lead_sub_type',
                title: 'Lead Sub Type'
            },{
                id: 'lead_type',
                title: 'Lead Type'
            },{
                id: 'homeowner_status',
                title: 'Homeowner Status'
            },{
                id: 'marital_status',
                title: 'Marital Status'
            },{
                id: 'out_of_service_area',
                title: 'Out of Service Area (Yes/No)'
            },{
                id: 'notes',
                title: 'Notes'
            },{
                id: 'occupational_status',
                title: 'Occupational Status'
            },{
                id: 'phone_number',
                title: 'Phone Number'
            },{
                id: 'priority',
                title: 'Priority'
            },{
                id: 'survey_credit',
                title: 'Survey'
            },{
                id: 'spouse_email_address',
                title: 'Spouse Email Address'
            },{
                id: 'spouse_first_name',
                title: 'Spouse First Name'
            },{
                id: 'spouse_last_name',
                title: 'Spouse Last Name'
            },{
                id: 'spouse_phone_number',
                title: 'Spouse Phone Number'
            },{
                id: 'administrative_area_level_1',
                title: 'State/Province'
            },{
                id: 'status',
                title: 'Status'
            },{
                id: 'street_address_1',
                title: 'Street Address'
            },{
                id: 'postal_code',
                title: 'Zipcode'
            }];

            // add custom values to default values
            targets = targets.concat(response.custom_lead_map_values).sort((a,b) => {
                return a.title.localeCompare(b.title);
            });

            // update local state with values
            setItems(targets);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the details for this survey. ${e.message || 'An unknown error occurred'}`,
                onClick: setLayerState.bind(this, 'close')
            });
        }
    }

    const setupTarget = () => {
        setEdits({
            collectors: preferences.collectors[survey.id] || {},
            lead_map: preferences.lead_maps[survey.id] || {},
        });
    }

    useEffect(() => {
        fetchDetails();
        setupTarget();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={survey.title}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            removePadding: true,
            sizing: 'medium'
        }}>
            {getContent()}
        </Layer>
    )
}
